import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { identityAPI, taxEngineAPI } from "../../services/api";
import { hasPermission } from "../../rbac";
import { useAuth } from "../../contexts/AuthContext";
import {
  accountClassLabel,
  apiErrorMessage,
  defaultFinancialYear,
  demandAmountBreakdown,
  demandBalance,
  demandSubjectName,
  demandSubjectReference,
  demandTaxName,
  groupDemandsByAccount,
  groupDemandsByFinancialYear,
  makeIdempotencyKey,
  summarizeDemands,
  money,
  paymentModeLabel,
  saveBlob,
  shortDate,
  statusLabel,
  subjectTypeLabel,
} from "../tax/taxWorkspace";
import { accountSubjectDisplay } from "./taxAccountUi";
import {
  canCancelDemand,
  demandCancellationPayload,
} from "./demandRevisionUi";
import TaxPdfViewer from "../../components/tax/TaxPdfViewer";
import TaxStatutoryDisclosure from "../../components/tax/TaxStatutoryDisclosure";
import TaxPricingRuleEditor from "./TaxPricingRuleEditor";
import AdminTaxRuleFormScreen from "./AdminTaxRuleFormScreen";
import ExcelHouseholdTaxImportModal from "../../components/tax/ExcelHouseholdTaxImportModal";
import { buildAnnualAssessmentView } from "./annualAssessmentUi";
import "./AdminTaxWorkspace.css";
import "./AdminTaxWorkspaceIntegrity.css";
import "../tax/TaxDemandDocuments.css";

const REGISTRY_REQUEST_LABELS = {
  new_property: "नवीन किंवा आकारणी न झालेली मालमत्ता",
  new_business: "नवीन व्यवसाय नोंद",
  property_correction: "मालमत्ता नोंद दुरुस्ती",
  ownership_transfer: "मालकी हस्तांतरण",
  inheritance: "वारस नोंद",
  construction_change: "बांधकाम बदल",
  demolition: "पाडकामाची नोंद",
  business_correction: "व्यवसाय नोंद दुरुस्ती",
  business_closure: "व्यवसाय बंद नोंद",
  other: "इतर नोंदणी सहाय्य",
};

const APPEAL_REASON_LABELS = {
  WRONG_ASSESSMENT: "चुकीची कर आकारणी",
  WRONG_OWNER: "मालक किंवा कर-जवाबदार व्यक्ती चुकीची",
  WRONG_PROPERTY_DETAILS: "मालमत्ता तपशील चुकीचा",
  WRONG_BUSINESS_DETAILS: "व्यवसाय तपशील चुकीचा",
  PAYMENT_NOT_REFLECTED: "भरलेली रक्कम दिसत नाही",
  EXEMPTION_NOT_APPLIED: "मंजूर सवलत लागू नाही",
  DUPLICATE_DEMAND: "दुबार मागणी",
  PENALTY_DISPUTE: "दंडाबाबत हरकत",
  OTHER: "इतर हरकत",
};

const REPORT_AGING_LABELS = {
  not_due: "अद्याप देय नाही",
  days_1_30: "१ ते ३० दिवस थकीत",
  days_31_60: "३१ ते ६० दिवस थकीत",
  days_61_90: "६१ ते ९० दिवस थकीत",
  days_91_plus: "९१ दिवसांपेक्षा अधिक थकीत",
};

const reportGroupLabel = (dimension, value) => {
  if (!value || value === "Unspecified") return "माहिती उपलब्ध नाही";
  if (dimension === "subject_type") return subjectTypeLabel(value);
  if (dimension === "account_class") return accountClassLabel(value);
  return value;
};

const assessmentFinancialYears = (date = new Date()) => {
  const currentStart = date.getMonth() >= 3 ? date.getFullYear() : date.getFullYear() - 1;
  return Array.from({ length: 8 }, (_, index) => {
    const start = currentStart + 1 - index;
    return `${start}-${String(start + 1).slice(-2)}`;
  });
};

const json = (value, fallback = {}) => {
  try {
    return value?.trim() ? JSON.parse(value) : fallback;
  } catch {
    throw new Error("JSON संरचना वैध नाही.");
  }
};

const formatFormulaConfigSummary = (version) => {
  const type = version?.formula_type;
  const config = version?.formula_config || {};

  // Construction Class Rates (e.g. MH_PROPERTY_TAX with RCC, Kachha, etc.)
  if (type === "property_class_rate" || config.class_field || config.rates) {
    const classLabels = {
      kachha: "कच्चे बांधकाम",
      semi_paka: "पक्के / मध्यम",
      load_bearing: "लोड बेरिंग",
      rcc: "आरसीसी (RCC)",
      open_plot: "मोकळी जागा / प्लॉट",
    };
    const rates = config.rates || {};
    const rateList = Object.entries(rates).map(([key, val]) => ({
      label: classLabels[key] || key,
      rate: `${val}% (दर शेकडा)`,
    }));
    return {
      title: "बांधकाम वर्गवारीनुसार दर शेकडा कर",
      detail: "मूल्यांकनावर (Assessed Value) बांधकाम प्रकारानुसार दर लागू",
      classRates: rateList,
      icon: "🏗️",
    };
  }

  if (
    type === "fixed" ||
    config.calculation_type === "fixed" ||
    (config.amount !== undefined && !config.rate_percent)
  ) {
    return {
      title: "निश्चित आकारणी रक्कम",
      detail: `₹${Number(config.amount || config.fixed_amount || 0).toLocaleString("en-IN")} (निश्चित वार्षिक दर)`,
      icon: "💵",
    };
  }

  if (
    type === "percentage" ||
    type === "rate_percent" ||
    config.calculation_type === "rate_percent" ||
    config.rate_percent !== undefined
  ) {
    const fieldLabel =
      config.base_field === "annual_rental_value" || config.base_field === "annual_value"
        ? "वार्षिक मूल्य (ARV)"
        : config.base_field === "capital_value"
        ? "भांडवली मूल्य (Capital Value)"
        : config.base_field === "assessed_value"
        ? "मूल्यांकन (Assessed Value)"
        : config.base_field === "annual_turnover"
        ? "वार्षिक उलाढाल"
        : config.base_field || "मूल्यांकन";
    return {
      title: "टक्केवारी आधारित कर दर",
      detail: `${config.rate_percent || 0}% (${fieldLabel} च्या)`,
      icon: "📊",
    };
  }

  if (
    type === "area_rate" ||
    config.calculation_type === "area_rate" ||
    config.rate_per_unit !== undefined
  ) {
    const areaLabel =
      config.area_field === "built_up_area_sqft"
        ? "बांधकाम क्षेत्रफळ (Sq.Ft)"
        : config.area_field === "plot_area_sqft"
        ? "प्लॉट क्षेत्रफळ (Sq.Ft)"
        : config.area_field || "क्षेत्रफळ";
    return {
      title: "क्षेत्रफळानुसार दर",
      detail: `₹${config.rate_per_unit || 0} प्रति चौ.फूट (${areaLabel} आधारित)`,
      icon: "📏",
    };
  }

  if (
    type === "slab" ||
    config.calculation_type === "slab" ||
    Array.isArray(config.slabs)
  ) {
    const slabs = Array.isArray(config.slabs) ? config.slabs : [];
    const isProgressive = (config.mode || "progressive") === "progressive";
    const baseField = config.base_field || "";
    const isCurrency = [
      "annual_rental_value",
      "annual_value",
      "arv",
      "assessed_value",
      "capital_value",
      "annual_turnover",
      "gross_income",
    ].includes(baseField);
    const isArea = [
      "built_up_area_sqft",
      "carpet_area_sqft",
      "land_area_sqft",
      "plot_area_sqft",
      "premise_area_sqft",
      "signage_area_sqft",
    ].includes(baseField);
    const isCount = [
      "employee_count",
      "employees_count",
      "active_location_count",
    ].includes(baseField);

    const baseName =
      baseField === "annual_rental_value" || baseField === "arv"
        ? "ARV"
        : baseField === "assessed_value" || baseField === "capital_value"
        ? "भांडवली मूल्य"
        : baseField === "annual_turnover" || baseField === "gross_income"
        ? "वार्षिक उलाढाल"
        : baseField === "built_up_area_sqft"
        ? "बांधकाम क्षेत्रफळ"
        : baseField === "premise_area_sqft"
        ? "व्यवसाय परिसर"
        : baseField === "employee_count"
        ? "कर्मचारी संख्या"
        : baseField || "मूल्यांकन";

    const formattedSlabs = slabs.map((s) => {
      let limitLabel = "पुढील सर्व";
      if (s.up_to !== null && s.up_to !== undefined && s.up_to !== "") {
        const num = Number(s.up_to).toLocaleString("en-IN");
        limitLabel = isCurrency
          ? `₹${num} पर्यंत`
          : isArea
          ? `${num} चौ.फूट पर्यंत`
          : isCount
          ? `${num} पर्यंत`
          : `${num} पर्यंत`;
      }
      let rateLabel = "";
      if (s.rate_percent !== null && s.rate_percent !== undefined) {
        rateLabel = `${s.rate_percent}%`;
      } else if (s.rate_per_unit !== null && s.rate_per_unit !== undefined) {
        rateLabel = isArea ? `₹${s.rate_per_unit}/चौ.फूट` : `₹${s.rate_per_unit}/नग`;
      } else if (s.amount !== null && s.amount !== undefined) {
        rateLabel = `₹${Number(s.amount).toLocaleString("en-IN")} ठोक`;
      } else if (s.value !== undefined) {
        rateLabel = `₹${s.value}`;
      }
      return { limitLabel, rateLabel };
    });

    return {
      title: isProgressive
        ? "टप्पा रचना (Progressive Slabs)"
        : "टप्पा रचना (Flat Bracket Slabs)",
      detail: `${slabs.length} टप्पे — ${isProgressive ? "उत्तरोत्तर दर" : "सलग टप्पा"} (${baseName} आधारित)`,
      formattedSlabs,
      icon: "📈",
    };
  }

  if (type === "expression" || config.expression) {
    return {
      title: "गणितीय सूत्र समीकरण (Formula Expression)",
      detail: config.expression || "सानुकूल सूत्र",
      icon: "🧮",
    };
  }

  return {
    title: "कर आकारणी नियम",
    detail:
      Object.keys(config).length > 0
        ? Object.entries(config)
            .map(
              ([k, v]) =>
                `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`,
            )
            .join(" | ")
        : "आकारणी दर कॉन्फिगर केलेला नाही",
    icon: "📋",
  };
};

const exemptionValues = (form) => {
  const percentage = form.get("percentage");
  const fixedAmount = form.get("fixed_amount");
  if (Boolean(percentage) === Boolean(fixedAmount))
    throw new Error("सवलतीची टक्केवारी किंवा निश्चित रक्कम यापैकी एकच भरा.");
  return {
    percentage: percentage || undefined,
    fixed_amount: percentage ? undefined : fixedAmount,
  };
};

const Modal = ({ title, onClose, children, error }) => (
  <div className="atx-backdrop" onMouseDown={onClose}>
    <section
      className="atx-modal"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <header>
        <h2>{title}</h2>
        <button type="button" onClick={onClose} aria-label="बंद करा">
          ×
        </button>
      </header>
      {error && (
        <div className="atx-modal-error" role="alert">
          <span className="atx-modal-error-icon" aria-hidden="true">⚠️</span>
          <div className="atx-modal-error-text">
            <strong>त्रुटी:</strong> {error}
          </div>
        </div>
      )}
      {children}
    </section>
  </div>
);
const Status = ({ value }) => (
  <span className={`atx-status ${value}`}>{statusLabel(value)}</span>
);

const GridActionIcon = ({ name }) => {
  const paths = {
    view: (
      <>
        <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6S2.5 12 2.5 12Z" />
        <circle cx="12" cy="12" r="2.75" />
      </>
    ),
    adjust: (
      <>
        <path d="M4 7h10M18 7h2M4 17h2M10 17h10M14 4v6M10 14v6" />
        <circle cx="16" cy="7" r="2" />
        <circle cx="8" cy="17" r="2" />
      </>
    ),
    approve: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="m8 12 3 3 5-6" />
      </>
    ),
    payment: (
      <>
        <path d="M4 6.5h14a2 2 0 0 1 2 2v9H4a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2h12" />
        <path d="M16 11h4M7 11v4M5 13h4" />
      </>
    ),
    cancel: (
      <>
        <circle cx="12" cy="12" r="8.5" />
        <path d="m9 9 6 6M15 9l-6 6" />
      </>
    ),
    download: (
      <>
        <path d="M12 3v11" />
        <path d="m7.5 10 4.5 4.5 4.5-4.5" />
        <path d="M4 17.5V21h16v-3.5" />
      </>
    ),
    refund: (
      <>
        <path d="M7 7H3v-4" />
        <path d="M3.5 7a9 9 0 1 1-1 8" />
        <path d="M9 10.5h6M9 14h6" />
      </>
    ),
    whatsapp: (
      <>
        <path d="M12 2C6.477 2 2 6.477 2 12c0 1.9.54 3.67 1.47 5.17L2 22l4.83-1.47A9.958 9.958 0 0 0 12 22c5.523 0 10-4.477 10-10S17.523 2 12 2z" />
        <path d="M8.5 9.5s.5-1 1.5-1 1.5 1 1.5 1l-1 2s2.5 2.5 3 3l2-1s1 .5 1 1.5-.5 2-1.5 2-5-2-6.5-3.5S7.5 10.5 8.5 9.5z" />
      </>
    ),
  };
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {paths[name]}
    </svg>
  );
};

const rulePeriodsOverlap = (left, right) => (
  String(left.effective_from) <= String(right.effective_to || "9999-12-31")
  && String(right.effective_from) <= String(left.effective_to || "9999-12-31")
);

const ActionSpinner = () => <span className="atx-action-spinner" aria-hidden="true" />;

const AccountProvisionRecords = ({ title, records, tone }) => {
  if (!records?.length) return null;
  return (
    <details className={`atx-account-provision-records ${tone}`} open={tone !== "existing"}>
      <summary>
        <span>{title}</span>
        <b>{records.length}</b>
      </summary>
      <div>
        {records.map((record, index) => (
          <article key={`${record.account_number || record.reference}-${index}`}>
            <div>
              <strong>{record.name}</strong>
              <small>{[record.reference, record.detail].filter(Boolean).join(" · ")}</small>
            </div>
            <div>
              {record.account_number && <b>{record.account_number}</b>}
              {record.message && <strong>{record.message}</strong>}
              {record.resolution && <small>काय करावे: {record.resolution}</small>}
            </div>
          </article>
        ))}
      </div>
    </details>
  );
};

const AccountProvisionResult = ({ result, onClose }) => {
  if (!result) return null;
  const groups = [
    ["property", "मालमत्ता कर खाती"],
    ["business", "व्यवसाय कर खाती"],
  ];
  return (
    <section className="atx-account-provision-result" aria-live="polite">
      <header>
        <div>
          <span>एक-क्लिक खाते निर्मिती पूर्ण</span>
          <h3>पात्र नोंदींसाठी कर खाती अद्ययावत झाली</h3>
          <p>आधीपासून असलेली खाती सुरक्षितपणे वगळली आहेत; दुबार खाती तयार केलेली नाहीत.</p>
        </div>
        <button type="button" onClick={onClose} aria-label="निकाल बंद करा">×</button>
      </header>
      <div className="atx-account-provision-kpis">
        <div><span>तपासलेल्या नोंदी</span><b>{result.totals?.scanned || 0}</b></div>
        <div className="created"><span>नवीन खाती</span><b>{result.totals?.created || 0}</b></div>
        <div><span>आधीपासून उपलब्ध</span><b>{result.totals?.existing || 0}</b></div>
        <div className="attention"><span>दुरुस्ती आवश्यक</span><b>{result.totals?.needs_attention || 0}</b></div>
      </div>
      <div className="atx-account-provision-groups">
        {groups.map(([key, label]) => {
          const group = result[key] || {};
          return (
            <section key={key}>
              <h4>{label}</h4>
              <AccountProvisionRecords title="नवीन तयार केलेली खाती" records={group.created} tone="created" />
              <AccountProvisionRecords title="आधीपासून उपलब्ध खाती" records={group.existing} tone="existing" />
              <AccountProvisionRecords title="दुरुस्ती आवश्यक नोंदी" records={group.needs_attention} tone="attention" />
              {!group.created?.length && !group.existing?.length && !group.needs_attention?.length && (
                <p className="atx-account-provision-empty">या प्रकारची सक्रिय नोंद उपलब्ध नाही.</p>
              )}
            </section>
          );
        })}
      </div>
    </section>
  );
};

const AppealProgressForm = ({ appeal, initialStatus, busy, onCancel, onSubmit }) => {
  const [status, setStatus] = useState(initialStatus || "under_review");
  return (
    <form
      className="atx-form"
      onSubmit={(event) => {
        event.preventDefault();
        const form = new FormData(event.currentTarget);
        const hearingValue = form.get("hearing_at");
        onSubmit({
          version: appeal.version,
          status,
          note: form.get("note"),
          ...(status === "hearing"
            ? { hearing_at: new Date(hearingValue).toISOString() }
            : {}),
        });
      }}
    >
      <div className="atx-case-context">
        <strong>मागणी क्र. {appeal.demand?.demand_number}</strong>
        <span>
          {APPEAL_REASON_LABELS[appeal.reason_code] ||
            appeal.reason_code?.replaceAll("_", " ") ||
            "कर आकारणीविषयी अपील"}
        </span>
      </div>
      <label>
        पुढील टप्पा
        <select value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="under_review">कागदपत्र व आकारणी तपासणी</option>
          <option value="hearing">सुनावणी निश्चित करा</option>
        </select>
      </label>
      {status === "hearing" && (
        <label>
          सुनावणी दिनांक व वेळ
          <input name="hearing_at" type="datetime-local" required />
        </label>
      )}
      <label>
        कार्यालयीन तपासणी नोंद
        <textarea
          name="note"
          required
          minLength="5"
          placeholder="तपासलेली कागदपत्रे, कर गणना किंवा सुनावणीबाबतची नोंद लिहा."
        />
      </label>
      <p className="atx-form-guidance">
        या टप्प्यामुळे अंतिम कर रक्कम बदलत नाही. अंतिम निर्णय स्वतंत्रपणे नोंदवावा लागेल.
      </p>
      <footer>
        <button type="button" onClick={onCancel}>रद्द करा</button>
        <button className="atx-primary" disabled={busy}>टप्पा जतन करा</button>
      </footer>
    </form>
  );
};

const AppealDecisionForm = ({ appeal, busy, onCancel, onSubmit }) => {
  const [decision, setDecision] = useState("rejected");
  const requiresAdjustment = decision !== "rejected";
  return (
    <form
      className="atx-form"
      onSubmit={(event) => {
        event.preventDefault();
        const form = new FormData(event.currentTarget);
        const payload = {
          version: appeal.version,
          status: decision,
          note: form.get("note"),
        };
        if (requiresAdjustment) {
          payload.adjustment = {
            adjustment_type: form.get("adjustment_type"),
            amount: form.get("amount"),
            reason_code: "APPEAL_DECISION",
            reason: form.get("note"),
          };
        }
        onSubmit(payload);
      }}
    >
      <div className="atx-case-context">
        <strong>मागणी क्र. {appeal.demand?.demand_number}</strong>
        <span>
          एकूण {money(appeal.demand?.total_amount)} · भरले{" "}
          {money(appeal.demand?.paid_amount)} · बाकी{" "}
          {money(appeal.demand?.balance_amount)}
        </span>
      </div>
      <label>
        अंतिम निर्णय
        <select value={decision} onChange={(event) => setDecision(event.target.value)}>
          <option value="rejected">अपील नामंजूर</option>
          <option value="allowed">अपील पूर्ण मंजूर</option>
          <option value="partially_allowed">अपील अंशतः मंजूर</option>
        </select>
      </label>
      <label>
        कारणासह निर्णय
        <textarea
          name="note"
          required
          minLength="5"
          placeholder="पुरावे, लागू कर सूत्र आणि निर्णयाचा स्पष्ट आधार लिहा."
        />
      </label>
      {requiresAdjustment && (
        <div className="atx-form-section">
          <h3 className="atx-form-section-title">मंजूर आर्थिक ॲडजस्टमेंट</h3>
          <label>
            ॲडजस्टमेंट प्रकार
            <select name="adjustment_type">
              <option value="credit">जमा ॲडजस्टमेंट</option>
              <option value="waiver">माफी</option>
              <option value="write_off">निर्लेखन</option>
            </select>
          </label>
          <label>
            मंजूर रक्कम
            <input
              name="amount"
              type="number"
              min=".01"
              max={appeal.demand?.balance_amount || undefined}
              step=".01"
              required
            />
          </label>
        </div>
      )}
      <p className="atx-warning">
        मंजूर किंवा अंशतः मंजूर निर्णयासाठी तयार होणारे ॲडजस्टमेंट दुसऱ्या अधिकृत
        अधिकाऱ्याने मंजूर केल्यानंतरच मागणीची बाकी बदलेल.
      </p>
      <footer>
        <button type="button" onClick={onCancel}>रद्द करा</button>
        <button className="atx-primary" disabled={busy}>अंतिम निर्णय नोंदवा</button>
      </footer>
    </form>
  );
};

const DemandPaymentHistory = ({ demand, compact = false }) => {
  const payments = demand.payments || [];
  if (!payments.length) return null;
  if (compact) {
    const payment = payments[0];
    const detail = [
      money(payment.amount),
      shortDate(payment.paid_at),
      paymentModeLabel(payment.payment_mode),
      payment.transaction_id ? `व्यवहार क्र. ${payment.transaction_id}` : "",
    ]
      .filter(Boolean)
      .join(" · ");
    return (
      <small className="atx-payment-compact" title={detail}>
        {shortDate(payment.paid_at)} · {paymentModeLabel(payment.payment_mode)}
        {payments.length > 1 ? ` · +${payments.length - 1}` : ""}
      </small>
    );
  }
  return (
    <div className="atx-payment-history">
      {payments.map((payment, index) => (
        <small key={payment.receipt_uid || payment.transaction_id || index}>
          <b>{money(payment.amount)}</b> · {shortDate(payment.paid_at)} ·{" "}
          {paymentModeLabel(payment.payment_mode)}
          {(payment.provider === "razorpay" ||
            payment.payment_mode === "online") &&
          payment.transaction_id ? (
            <> · व्यवहार क्र. <code>{payment.transaction_id}</code></>
          ) : null}
        </small>
      ))}
    </div>
  );
};

const runTypeLabel = (value) =>
  ({ manual: "स्वहस्ते", supplementary: "पूरक", scheduled: "नियोजित" })[
    value
  ] ||
  value ||
  "—";

const AnnualAssessmentRecord = ({ item, kind, tenantSlug }) => (
  <article
    className={`atx-assessment-item ${kind}`}
    title={item.detail || item.name}
  >
    <div className="atx-assessment-item-main">
      <strong>{item.name}</strong>
      {item.reference && <small>{item.reference}</small>}
      {item.detail && <p>{item.detail}</p>}
      {kind === "error" && item.missingFields?.length > 0 && (
        <p className="atx-assessment-missing">
          <strong>अपूर्ण माहिती:</strong> {item.missingFields.join(", ")}
        </p>
      )}
      {kind === "error" && item.issueDetail && (
        <small className="atx-assessment-technical">कारण: {item.issueDetail}</small>
      )}
      {kind === "error" && item.subjectUid && item.type === "property" && (
        <Link
          className="atx-assessment-edit-link"
          to={
            item.missingFieldKeys?.some(field =>
              ["assessed_value", "annual_rental_value", "land_value", "building_value"].includes(field)
            )
              ? `/admin/${tenantSlug}/properties/${item.subjectUid}`
              : `/admin/${tenantSlug}/properties/${item.subjectUid}/edit`
          }
        >
          ✎ मालमत्ता दुरुस्त करा
        </Link>
      )}
      {kind === "error" && item.subjectUid && item.type === "business" && (
        <Link
          className="atx-assessment-edit-link"
          to={`/admin/${tenantSlug}/businesses/${item.subjectUid}`}
        >
          ✎ व्यवसाय संपादित करा
        </Link>
      )}
    </div>
    <div className="atx-assessment-charge">
      {item.taxName && <span>{item.taxName}</span>}
      {kind === "new" && <b>{money(item.amount)}</b>}
      {kind === "skipped" && item.existingAmount > 0 && (
        <>
          <b>{money(item.existingAmount)}</b>
          <small>
            भरले {money(item.existingPaidAmount)} · बाकी{" "}
            {money(item.existingBalanceAmount)}
          </small>
        </>
      )}
    </div>
  </article>
);

const AnnualAssessmentSubjectList = ({ title, items, kind, tenantSlug }) => {
  if (!items.length) return null;

  if (kind === "error") {
    const issues = [...items.reduce((groups, item) => {
      const key = `${item.title}|${item.resolution}`;
      if (!groups.has(key)) {
        groups.set(key, {
          title: item.title,
          resolution: item.resolution,
          items: [],
        });
      }
      groups.get(key).items.push(item);
      return groups;
    }, new Map()).values()];

    return (
      <section className="atx-assessment-list error">
        <h4>
          {title} <span>{items.length}</span>
        </h4>
        <div className="atx-assessment-issues">
          {issues.map((issue, issueIndex) => (
            <details
              className="atx-assessment-issue"
              key={`${issue.title}-${issueIndex}`}
              open={issues.length === 1}
            >
              <summary>
                <span>{issue.title}</span>
                <b>{issue.items.length} नोंदी</b>
              </summary>
              <div className="atx-assessment-issue-resolution">
                <strong>काय करावे:</strong> {issue.resolution}
              </div>
              <div className="atx-assessment-issue-records">
                {issue.items.map((item, index) => (
                  <AnnualAssessmentRecord
                    item={item}
                    kind="error"
                    tenantSlug={tenantSlug}
                    key={`${item.name}-${item.reference}-${index}`}
                  />
                ))}
              </div>
            </details>
          ))}
        </div>
      </section>
    );
  }

  const existingTotal = items.reduce(
    (sum, item) => sum + Number(item.existingAmount || 0),
    0,
  );
  const records = (
    <div className="atx-assessment-items">
      {items.map((item, index) => (
        <AnnualAssessmentRecord
          item={item}
          kind={kind}
          tenantSlug={tenantSlug}
          key={`${item.name}-${item.taxName}-${index}`}
        />
      ))}
    </div>
  );

  if (kind === "skipped") {
    return (
      <details className="atx-assessment-list atx-assessment-existing">
        <summary>
          <span>
            {title} <b>{items.length}</b>
          </span>
          <strong>
            विद्यमान आकारणी एकूण {money(existingTotal)}
          </strong>
        </summary>
        <p>
          या नोंदींच्या मागण्या आधीच उपलब्ध आहेत किंवा या वर्षासाठी नवीन
          आकारणी लागू नाही. तपशील पाहण्यासाठी यादी उघडा.
        </p>
        {records}
      </details>
    );
  }

  return (
    <section className={`atx-assessment-list ${kind}`}>
      <h4>
        {title} <span>{items.length}</span>
      </h4>
      {records}
    </section>
  );
};

const AnnualAssessmentGroup = ({ title, icon, group, completed = false, tenantSlug }) => {
  const total = group.newItems.length + group.skipped.length + group.errors.length;
  return (
    <article className="atx-assessment-group">
      <header>
        <div>
          <span className="atx-assessment-group-icon">{icon}</span>
          <div>
            <h3>{title}</h3>
            <p>{total ? `${total} कर नोंदी तपासल्या` : "तपासण्यासाठी नोंद नाही"}</p>
          </div>
        </div>
        <div className="atx-assessment-group-counts">
          <span className="new">{group.newItems.length} नवीन</span>
          <span className="skipped">{group.skipped.length} वगळल्या</span>
          <span className="error">{group.errors.length} दुरुस्ती</span>
        </div>
      </header>
      {!total && (
        <div className="atx-assessment-empty">
          या आर्थिक वर्षासाठी पात्र सक्रिय नोंद किंवा लागू कर सूत्र आढळले नाही.
        </div>
      )}
      <AnnualAssessmentSubjectList
        title={completed ? "नव्याने तयार झालेल्या मागण्या" : "नवीन तयार होणाऱ्या मागण्या"}
        items={group.newItems}
        kind="new"
        tenantSlug={tenantSlug}
      />
      <AnnualAssessmentSubjectList
        title="आधीच असलेल्या किंवा लागू नसलेल्या नोंदी"
        items={group.skipped}
        kind="skipped"
        tenantSlug={tenantSlug}
      />
      <AnnualAssessmentSubjectList
        title="दुरुस्ती आवश्यक"
        items={group.errors}
        kind="error"
        tenantSlug={tenantSlug}
      />
    </article>
  );
};

const AnnualAssessmentResult = ({
  run,
  accounts,
  rules,
  busy,
  permissions,
  tenantSlug,
  onApprove,
  onIssue,
  onClear,
}) => {
  if (!run) return null;
  const groups = buildAnnualAssessmentView(run, accounts, rules);
  const otherTotal =
    groups.other.newItems.length +
    groups.other.skipped.length +
    groups.other.errors.length;
  const newCount =
    groups.property.newItems.length +
    groups.business.newItems.length +
    groups.other.newItems.length;
  const skippedCount =
    groups.property.skipped.length +
    groups.business.skipped.length +
    groups.other.skipped.length;
  const existingTotal = [
    ...groups.property.skipped,
    ...groups.business.skipped,
    ...groups.other.skipped,
  ].reduce((sum, item) => sum + Number(item.existingAmount || 0), 0);
  const errorCount =
    groups.property.errors.length +
    groups.business.errors.length +
    groups.other.errors.length;

  return (
    <section className="atx-assessment-result" aria-live="polite">
      <header className="atx-assessment-result-head">
        <div>
          <span>आर्थिक वर्ष {run.financial_year}</span>
          <h2>कर आकारणीचा निकाल</h2>
          <p>मालमत्ता व व्यवसायनिहाय तपासणीचा समजण्यास सोपा सारांश</p>
        </div>
        <div className="atx-assessment-result-status">
          <Status value={run.status} />
          <button type="button" onClick={onClear} aria-label="निकाल बंद करा">
            ×
          </button>
        </div>
      </header>

      <div className="atx-assessment-kpis">
        <div className="new">
          <span>नवीन मागण्या</span>
          <strong>{newCount}</strong>
        </div>
        <div className="skipped">
          <span>आधीच्या किंवा वगळलेल्या</span>
          <strong>{skippedCount}</strong>
          <small>विद्यमान आकारणी {money(existingTotal)}</small>
        </div>
        <div className="error">
          <span>दुरुस्ती आवश्यक</span>
          <strong>{errorCount}</strong>
        </div>
        <div className="amount">
          <span>नवीन आकारणी एकूण</span>
          <strong>{money(run.preview_total)}</strong>
        </div>
      </div>

      <div className="atx-assessment-columns">
        <AnnualAssessmentGroup
          title="मालमत्ता कर"
          icon="⌂"
          group={groups.property}
          completed={run.status === "completed"}
          tenantSlug={tenantSlug}
        />
        <AnnualAssessmentGroup
          title="व्यवसाय कर"
          icon="▣"
          group={groups.business}
          completed={run.status === "completed"}
          tenantSlug={tenantSlug}
        />
      </div>

      {otherTotal > 0 && (
        <AnnualAssessmentGroup
          title="इतर सेवा कर"
          icon="◈"
          group={groups.other}
          completed={run.status === "completed"}
          tenantSlug={tenantSlug}
        />
      )}

      <footer className="atx-assessment-result-actions">
        <div>
          {run.status === "completed" && Number(run.error_count) === 0 ? (
            <>
              <strong>सर्व पात्र कर मागण्या यशस्वीपणे जारी झाल्या.</strong>
              <span>पुढील प्रक्रिया चालवल्यास या मागण्या “आधीच अस्तित्वात” म्हणून सुरक्षितपणे वगळल्या जातील.</span>
            </>
          ) : Number(run.generated_count) > 0 && Number(run.error_count) > 0 ? (
            <>
              <strong>
                {run.status === "completed"
                  ? "पात्र मागण्या जारी झाल्या; त्रुटी असलेल्या नोंदी वगळल्या आहेत."
                  : "पात्र मागण्या स्वतंत्रपणे मंजूर आणि जारी करता येतील."}
              </strong>
              <span>
                त्रुटी असलेल्या नोंदींसाठी मागणी तयार होणार नाही. त्या दुरुस्त
                करून ही प्रक्रिया पुन्हा चालवल्यावर फक्त उर्वरित मागण्या तयार होतील.
              </span>
            </>
          ) : Number(run.error_count) > 0 ? (
            <>
              <strong>सध्या कोणतीही पात्र नवीन मागणी नाही.</strong>
              <span>वरील “काय करावे” सूचना पूर्ण करून प्रक्रिया पुन्हा चालवा.</span>
            </>
          ) : Number(run.generated_count) === 0 ? (
            <>
              <strong>नवीन मागणी तयार करण्याची आवश्यकता नाही.</strong>
              <span>या वर्षातील लागू मागण्या आधीच तयार आहेत किंवा कोणतीही नवीन आकारणी लागू नाही.</span>
            </>
          ) : (
            <>
              <strong>Preview तयार आहे.</strong>
              <span>रक्कम आणि नोंदी तपासल्यानंतर अधिकृत मंजुरी द्या.</span>
            </>
          )}
        </div>
        {run.status === "preview_ready" &&
          Number(run.generated_count) > 0 &&
          permissions.approve && (
            <button className="atx-primary" disabled={busy} onClick={onApprove}>
              Preview मंजूर करा
            </button>
          )}
        {run.status === "approved" && permissions.issue && (
          <button className="atx-primary" disabled={busy} onClick={onIssue}>
            कर मागण्या जारी करा
          </button>
        )}
      </footer>
    </section>
  );
};

const frequencyLabel = (value) =>
  ({
    annual: "वार्षिक",
    half_yearly: "सहामाही",
    quarterly: "तिमाही",
    monthly: "मासिक",
    one_time: "एकदाच",
  })[value] ||
  value ||
  "—";
const formulaTypeLabel = (value) =>
  ({
    fixed: "निश्चित रक्कम",
    percentage: "टक्केवारी",
    area_rate: "क्षेत्रफळ दर",
    slab: "टप्पेनिहाय दर",
    expression: "गणिती सूत्र",
  })[value] ||
  value ||
  "—";
const adjustmentTypeLabel = (value) =>
  ({
    debit: "अतिरिक्त आकारणी",
    credit: "जमा ॲडजस्टमेंट",
    waiver: "माफी",
    write_off: "निर्लेखन",
  })[value] ||
  value ||
  "—";

const AdjustmentForm = ({ demand, busy, onCancel, onSubmit }) => {
  const [adjustmentType, setAdjustmentType] = useState("credit");
  const [amount, setAmount] = useState("");
  const [reasonCode, setReasonCode] = useState("MANUAL_CORRECTION");
  const [reason, setReason] = useState("");

  const originalTotal = Number(demand?.total_amount || 0);
  const alreadyPaid = Number(demand?.paid_amount || 0);
  const currentBalance = Number(demand?.balance_amount ?? Math.max(0, originalTotal - alreadyPaid));

  const parsedAmount = parseFloat(amount);
  const hasValidAmount = !isNaN(parsedAmount) && parsedAmount > 0;

  const isDebit = adjustmentType === "debit";
  const signedDelta = hasValidAmount ? (isDebit ? parsedAmount : -parsedAmount) : 0;
  const projectedTotal = Math.max(0, Math.round((originalTotal + signedDelta) * 100) / 100);
  const projectedBalance = Math.max(0, Math.round((projectedTotal - alreadyPaid) * 100) / 100);

  // Validation: maximum allowed reduction for credit/waiver/write_off is currentBalance
  const maxAllowedReduction = Math.max(0, Math.round((originalTotal - alreadyPaid) * 100) / 100);
  let validationError = "";
  if (hasValidAmount && !isDebit) {
    if (parsedAmount > maxAllowedReduction) {
      validationError = `कमाल अनुज्ञेय वजावट ₹${maxAllowedReduction.toFixed(2)} आहे. भरलेल्या रकमेपेक्षा (₹${alreadyPaid.toFixed(2)}) मागणी कमी करता येत नाही.`;
    }
  }

  const canSubmit = hasValidAmount && !validationError && reason.trim().length >= 5 && !busy;

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      adjustment_type: adjustmentType,
      amount: parsedAmount,
      reason_code: reasonCode,
      reason: reason.trim(),
    });
  };

  return (
    <form className="atx-form" onSubmit={handleSubmit}>
      <label>
        ॲडजस्टमेंट प्रकार
        <select
          name="adjustment_type"
          value={adjustmentType}
          onChange={(e) => setAdjustmentType(e.target.value)}
        >
          <option value="credit">जमा ॲडजस्टमेंट (Credit - मागणी कमी करा)</option>
          <option value="debit">अतिरिक्त आकारणी (Debit - मागणी वाढवा)</option>
          <option value="waiver">माफी (Waiver - दंड/कर सवलत)</option>
          <option value="write_off">निर्लेखन (Write-off - न वसूल होणारी)</option>
        </select>
      </label>

      <label>
        रक्कम (₹)
        <input
          name="amount"
          type="number"
          min="0.01"
          step="0.01"
          required
          placeholder="उदा. 250.00"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        {!isDebit && maxAllowedReduction > 0 && (
          <small className="atx-field-hint" style={{ color: "#0369a1", marginTop: "3px" }}>
            ℹ️ या खात्यावर कमाल अनुज्ञेय वजावट मर्यादा: <strong>{money(maxAllowedReduction)}</strong>
          </small>
        )}
      </label>

      {/* Live Financial Impact Preview Card */}
      <div className="atx-adjustment-preview-card">
        <div className="atx-adjustment-preview-header">
          <span className="atx-adjustment-preview-title">📊 आर्थिक प्रभावाचा आढावा (Impact Preview)</span>
          <span className={`atx-adjustment-badge ${isDebit ? "debit" : "credit"}`}>
            {isDebit ? "+ अतिरिक्त आकारणी (Debit)" : "- वजावट / सवलत (Credit)"}
          </span>
        </div>

        <div className="atx-adjustment-preview-grid">
          <div className="atx-adjustment-preview-col">
            <span className="atx-preview-label">सध्याची स्थिती (Current)</span>
            <div className="atx-preview-val-row">
              <span>एकूण मागणी:</span>
              <strong>{money(originalTotal)}</strong>
            </div>
            <div className="atx-preview-val-row">
              <span>भरलेली रक्कम:</span>
              <strong style={{ color: "#059669" }}>{money(alreadyPaid)}</strong>
            </div>
            <div className="atx-preview-val-row highlight">
              <span>शिल्लक बाकी:</span>
              <strong style={{ color: currentBalance > 0 ? "#b91c1c" : "#475569" }}>{money(currentBalance)}</strong>
            </div>
          </div>

          <div className="atx-adjustment-preview-arrow">➔</div>

          <div className="atx-adjustment-preview-col projected">
            <span className="atx-preview-label">सुधारित स्थिती (After Adjustment)</span>
            <div className="atx-preview-val-row">
              <span>नवीन एकूण मागणी:</span>
              <strong style={{ color: isDebit ? "#b45309" : "#0284c7" }}>
                {hasValidAmount ? money(projectedTotal) : "—"}
              </strong>
            </div>
            <div className="atx-preview-val-row">
              <span>भरलेली रक्कम:</span>
              <strong style={{ color: "#059669" }}>{money(alreadyPaid)}</strong>
            </div>
            <div className="atx-preview-val-row highlight">
              <span>नवीन शिल्लक बाकी:</span>
              <strong style={{ color: projectedBalance > 0 ? "#b91c1c" : "#15803d" }}>
                {hasValidAmount ? money(projectedBalance) : "—"}
              </strong>
            </div>
          </div>
        </div>

        {hasValidAmount && !validationError && (
          <div className="atx-adjustment-confirm-strip">
            ✓ <strong>बदलाची खात्री:</strong> एकूण मागणी {money(originalTotal)} वरून <strong>{money(projectedTotal)}</strong> होईल ({isDebit ? `+${money(parsedAmount)} वाढ` : `-${money(parsedAmount)} वजावट`}).
          </div>
        )}

        {validationError && (
          <div className="atx-adjustment-warning-strip" role="alert">
            ⚠️ {validationError}
          </div>
        )}
      </div>

      <label>
        कारण संकेतांक
        <input
          name="reason_code"
          required
          value={reasonCode}
          onChange={(e) => setReasonCode(e.target.value)}
        />
      </label>

      <label>
        कारण (किमान ५ अक्षरे)
        <textarea
          name="reason"
          required
          minLength={5}
          placeholder="उदा. ग्रामपंचायत ठराव क्र. १२ नुसार कर सवलत किंवा मोजमाप दुरुस्ती"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </label>

      <footer>
        <button type="button" onClick={onCancel} disabled={busy}>
          रद्द करा
        </button>
        <button className="atx-primary" disabled={!canSubmit}>
          {busy ? "लागू होत आहे…" : "ॲडजस्टमेंट लागू करा (तात्काळ मंजूर)"}
        </button>
      </footer>
    </form>
  );
};

const ManualCollectionForm = ({
  demand,
  busy,
  tenantSlug,
  onClose,
  onSubmit,
}) => {
  const [mode, setMode] = useState("cash");
  const [payerQuery, setPayerQuery] = useState("");
  const [payers, setPayers] = useState([]);

  // Lock taxpayer to the property/business account taxpayer by default
  const defaultTaxpayer = demand.account?.taxpayer
    ? {
        party_uid: demand.account.taxpayer.party_uid,
        legal_name: demand.account.taxpayer.name,
        name: demand.account.taxpayer.name,
        phone_last4: demand.account.taxpayer.phone
          ? demand.account.taxpayer.phone.slice(-4)
          : "",
        phone: demand.account.taxpayer.phone || "",
        primary_phone: demand.account.taxpayer.phone || "",
        party_type: demand.account.taxpayer.role_type || "individual",
      }
    : null;

  const [payer, setPayer] = useState(defaultTaxpayer);
  const [membershipUid, setMembershipUid] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const today = new Date().toISOString().slice(0, 10);
  const amountSummary = demandAmountBreakdown(demand);

  // If demand account taxpayer UID wasn't populated, attempt to find via name
  useEffect(() => {
    if (!payer && demand.account?.taxpayer?.name) {
      taxEngineAPI
        .searchCollectionPayers(tenantSlug, {
          q: demand.account.taxpayer.name.trim(),
        })
        .then((response) => {
          const matched =
            response.data?.payers?.find(
              (p) => p.party_uid === demand.account?.taxpayer?.party_uid,
            ) || response.data?.payers?.[0];
          if (matched) {
            setPayer(matched);
          }
        })
        .catch(() => {});
    }
  }, [demand, payer, tenantSlug]);

  const search = async () => {
    if (payerQuery.trim().length < 2) {
      setSearchError("किमान दोन अक्षरे भरा.");
      return;
    }
    setSearching(true);
    setSearchError("");
    try {
      const response = await taxEngineAPI.searchCollectionPayers(tenantSlug, {
        q: payerQuery.trim(),
      });
      setPayers(response.data.payers || []);
    } catch (err) {
      setSearchError(apiErrorMessage(err));
    } finally {
      setSearching(false);
    }
  };

  const submit = (event) => {
    event.preventDefault();
    if (!payer) {
      setSearchError("भरण्याची नोंद करण्यापूर्वी अचूक करदाता निवडा.");
      return;
    }
    const form = new FormData(event.currentTarget);
    onSubmit({
      allocations: [
        {
          demand_uid: demand.demand_uid,
          amount: form.get("amount") || undefined,
        },
      ],
      payment_mode: mode,
      payer_party_uid: payer.party_uid,
      payer_membership_uid: membershipUid || undefined,
      settlement_confirmed:
        mode === "upi" ? form.get("settlement_confirmed") === "on" : undefined,
      upi_reference: mode === "upi" ? form.get("upi_reference") : undefined,
      bank_name: ["cheque", "dd", "bank_transfer"].includes(mode)
        ? form.get("bank_name")
        : undefined,
      branch_name: ["cheque", "dd", "bank_transfer"].includes(mode)
        ? form.get("branch_name") || undefined
        : undefined,
      instrument_number: ["cheque", "dd"].includes(mode)
        ? form.get("instrument_number")
        : undefined,
      instrument_date: ["cheque", "dd"].includes(mode)
        ? form.get("instrument_date")
        : undefined,
      transfer_reference:
        mode === "bank_transfer" ? form.get("transfer_reference") : undefined,
      payment_date: ["upi", "bank_transfer"].includes(mode)
        ? form.get("payment_date")
        : undefined,
      notes: form.get("notes") || undefined,
    });
  };

  return (
    <form className="atx-form atx-payment-record-form" onSubmit={submit}>
      {/* 1. Rich Property / Business & Demand Context */}
      <div className="atx-payment-demand-summary">
        <div className="atx-pds-header">
          <div className="atx-pds-subject">
            <span className={`atx-subject-kind ${demand.account?.subject_type || ""}`}>
              {subjectTypeLabel(demand.account?.subject_type)}
            </span>
            <h3 className="atx-pds-title">{demandSubjectName(demand)}</h3>
            {demandSubjectReference(demand) &&
              !/^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(demandSubjectReference(demand)) && (
                <span className="atx-pds-ref">क्र. {demandSubjectReference(demand)}</span>
              )}
          </div>
          <div className="atx-pds-demand-badge">
            <span className="atx-pds-tax-name">{demandTaxName(demand)}</span>
            <span className="atx-pds-demand-no">मागणी क्र. {demand.demand_number}</span>
          </div>
        </div>
        <div className="atx-pds-meta-grid">
          <div className="atx-pds-meta-item">
            <span className="atx-pds-meta-label">कर आकारणी कालावधी</span>
            <strong className="atx-pds-meta-val">
              {shortDate(demand.period_start)} – {shortDate(demand.period_end)}
            </strong>
          </div>
          <div className="atx-pds-meta-item">
            <span className="atx-pds-meta-label">देय दिनांक (Due Date)</span>
            <strong className="atx-pds-meta-val">{shortDate(demand.due_date)}</strong>
          </div>
          <div className="atx-pds-meta-item">
            <span className="atx-pds-meta-label">मागणी स्थिती</span>
            <span className={`tax-status ${demand.status}`}>{statusLabel(demand.status)}</span>
          </div>
        </div>
      </div>

      {/* 2. Amount Summary */}
      <div className="atx-amount-summary">
        <span>
          मूळ कर बाकी{" "}
          <strong>{money(amountSummary.outstandingPrincipal)}</strong>
        </span>
        <span className="atx-penalty-text">
          नियम २०(३) दंड बाकी{" "}
          <strong>{money(amountSummary.outstandingPenalty)}</strong>
        </span>
        <span>
          दंडासह भरावयाची रक्कम <strong>{money(amountSummary.balance)}</strong>
        </span>
      </div>

      {/* 3. Locked Taxpayer Card */}
      {payer ? (
        <div className="atx-locked-payer-card">
          <div className="atx-locked-payer-badge">
            <span className="atx-lock-icon">🔒</span>
            <span>अधिकृत करदाता (नोंदणीकृत / बदलता येणार नाही)</span>
          </div>
          <div className="atx-locked-payer-info">
            <div className="atx-payer-avatar">
              {((payer?.legal_name || payer?.name || "क")[0]).toUpperCase()}
            </div>
            <div className="atx-locked-payer-details">
              <h4 className="atx-locked-payer-name">{payer?.legal_name || payer?.name}</h4>
              <div className="atx-locked-payer-meta">
                {(payer?.phone || payer?.primary_phone) && (
                  <span>📞 {payer.phone || payer.primary_phone}</span>
                )}
                {payer?.party_uid && (
                  <span className="atx-party-id">ID: {payer.party_uid.slice(0, 14)}…</span>
                )}
                {demand.account?.account_number && (
                  <span>खाते: {demand.account.account_number}</span>
                )}
              </div>
            </div>
          </div>
          <p className="atx-locked-payer-note">
            सदर कर पावती अधिकृत करदात्याच्या नावाने जारी केली जाईल आणि त्यांच्या नोंदणीकृत फोनवर WhatsApp द्वारे पाठवली जाईल.
          </p>
        </div>
      ) : (
        <fieldset className="atx-payer-picker">
          <legend>करदाता निवडा</legend>
          <div className="atx-inline-search">
            <input
              value={payerQuery}
              onChange={(event) => setPayerQuery(event.target.value)}
              placeholder="नाव, पक्ष क्रमांक किंवा फोन"
            />
            <button type="button" onClick={search} disabled={searching}>
              {searching ? "शोधत आहे…" : "शोधा"}
            </button>
          </div>
          {searchError && (
            <small className="atx-inline-error">{searchError}</small>
          )}
          <div className="atx-payer-results">
            {payers.map((item) => (
              <button
                type="button"
                className={payer?.party_uid === item.party_uid ? "selected" : ""}
                key={item.party_uid}
                onClick={() => {
                  setPayer(item);
                  setMembershipUid("");
                }}
              >
                <strong>{item.legal_name}</strong>
                <small>
                  {item.local_party_uid} · {item.party_type}
                  {item.phone_last4 ? ` · फोन ****${item.phone_last4}` : ""}
                </small>
              </button>
            ))}
          </div>
        </fieldset>
      )}

      {/* 4. Payment Fields */}
      <label>
        रक्कम
        <input
          name="amount"
          type="number"
          min=".01"
          max={demandBalance(demand)}
          step=".01"
          defaultValue={demandBalance(demand)}
          required
        />
      </label>
      <label>
        भरणा पद्धत
        <select
          name="payment_mode"
          value={mode}
          onChange={(event) => setMode(event.target.value)}
        >
          <option value="cash">रोख</option>
          <option value="upi">यूपीआय (बँकेकडून पुष्टी)</option>
          <option value="cheque">धनादेश</option>
          <option value="dd">मागणी धनाकर्ष</option>
          <option value="bank_transfer">बँक हस्तांतरण</option>
        </select>
      </label>
      {mode === "upi" && (
        <>
          <label>
            यूपीआय / बँक संदर्भ
            <input name="upi_reference" required minLength="3" />
          </label>
          <label>
            जमा दिनांक
            <input
              name="payment_date"
              type="date"
              required
              defaultValue={today}
            />
          </label>
          <label className="atx-check">
            <input name="settlement_confirmed" type="checkbox" required />{" "}
            ग्रामपंचायतीच्या बँक खात्यात रक्कम जमा झाल्याची पुष्टी आहे
          </label>
        </>
      )}
      {["cheque", "dd"].includes(mode) && (
        <>
          <label>
            बँकेचे नाव
            <input name="bank_name" required minLength="2" />
          </label>
          <label>
            शाखा (ऐच्छिक)
            <input name="branch_name" />
          </label>
          <label>
            {mode === "cheque" ? "धनादेश" : "मागणी धनाकर्ष"} क्रमांक
            <input name="instrument_number" required minLength="3" />
          </label>
          <label>
            दस्तऐवज दिनांक
            <input name="instrument_date" type="date" required />
          </label>
        </>
      )}
      {mode === "bank_transfer" && (
        <>
          <label>
            बँकेचे नाव
            <input name="bank_name" required minLength="2" />
          </label>
          <label>
            शाखा (ऐच्छिक)
            <input name="branch_name" />
          </label>
          <label>
            हस्तांतरण / यूटीआर संदर्भ
            <input name="transfer_reference" required minLength="3" />
          </label>
          <label>
            हस्तांतरण दिनांक
            <input
              name="payment_date"
              type="date"
              required
              defaultValue={today}
            />
          </label>
        </>
      )}
      <label>
        टीप
        <textarea name="notes" />
      </label>
      <p className="atx-warning">
        {["cheque", "dd", "bank_transfer"].includes(mode)
          ? "सध्या फक्त भरणा दस्तऐवजाची नोंद होईल. स्वतंत्र अधिकाऱ्याने रक्कम जमा झाल्याची पडताळणी करेपर्यंत मागणीची बाकी कमी होणार नाही किंवा पावती तयार होणार नाही."
          : "रोख किंवा पुष्टी झालेला यूपीआय भरणा तत्काळ जमा होईल. निवडलेल्या करदात्याचे नाव पावतीवर नोंदले जाईल."}
      </p>
      <footer>
        <button type="button" onClick={onClose}>
          रद्द करा
        </button>
        <button className="atx-primary" disabled={busy || !payer}>
          {["cheque", "dd", "bank_transfer"].includes(mode)
            ? "पडताळणीसाठी सादर करा"
            : "पुष्टी झालेला भरणा नोंदवा"}
        </button>
      </footer>
    </form>
  );
};

export default function AdminTaxWorkspace() {
  const { tenantSlug } = useParams();
  const { user } = useAuth();
  const [tab, setTab] = useState("accounts");
  const [showImportModal, setShowImportModal] = useState(false);
  const [ruleCategoryTab, setRuleCategoryTab] = useState("all");
  const [ruleFormScreen, setRuleFormScreen] = useState(null); // null | { mode: 'create', rule: null } | { mode: 'edit', rule }
  const [expandedRuleVersions, setExpandedRuleVersions] = useState({});
  const toggleRuleVersions = (ruleUid) => {
    setExpandedRuleVersions((prev) => ({
      ...prev,
      [ruleUid]: !prev[ruleUid],
    }));
  };
  const [accounts, setAccounts] = useState([]);
  const [rules, setRules] = useState([]);
  const [runs, setRuns] = useState([]);
  const [demands, setDemands] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [collectionsSubView, setCollectionsSubView] = useState("bills"); // 'bills' | 'receipts'
  const [exemptions, setExemptions] = useState([]);
  const [appeals, setAppeals] = useState([]);
  const [accessIssues, setAccessIssues] = useState([]);
  const [registryRequests, setRegistryRequests] = useState([]);
  const [report, setReport] = useState(null);
  const [reportFilters, setReportFilters] = useState({
    financial_year: defaultFinancialYear(),
    subject_type: "",
    account_class: "",
    ward_number: "",
    business_category: "",
    as_of: new Date().toISOString().slice(0, 10),
  });
  const [reconciliation, setReconciliation] = useState({
    payment_intents: [],
    manual_collection_attempts: [],
    receipt_actions: [],
  });
  const [filters, setFilters] = useState({
    q: "",
    status: "",
    subject_type: "",
    financial_year: "",
  });
  const [modal, setModal] = useState(null);
  const [assessmentYear, setAssessmentYear] = useState(defaultFinancialYear());
  const [assessmentResult, setAssessmentResult] = useState(null);
  const [accountProvisionResult, setAccountProvisionResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [receiptPreviewLoading, setReceiptPreviewLoading] = useState(false);
  const [demandNoticePreviewLoading, setDemandNoticePreviewLoading] =
    useState(false);
  const [documentDownloadKey, setDocumentDownloadKey] = useState("");
  const [reminderSendingKey, setReminderSendingKey] = useState("");

  useEffect(() => {
    if (!message && !error) return;
    const timer = setTimeout(() => {
      setMessage("");
      if (error) setError("");
    }, error ? 8000 : 5000);
    return () => clearTimeout(timer);
  }, [message, error]);

  useEffect(() => {
    if (modal) {
      setError("");
    }
  }, [modal]);

  const permissions = {
    configure: hasPermission(user, "tax_configuration", "manage"),
    approveConfig: hasPermission(user, "tax_configuration", "approve"),
    generate:
      hasPermission(user, "tax_assessments", "generate") ||
      hasPermission(user, "taxes", "create"),
    activateAccount:
      hasPermission(user, "tax_assessments", "generate") ||
      hasPermission(user, "taxes", "update"),
    closeAccount:
      hasPermission(user, "tax_assessments", "cancel") ||
      hasPermission(user, "taxes", "update"),
    preview: hasPermission(user, "tax_assessments", "preview"),
    approve: hasPermission(user, "tax_assessments", "approve"),
    issue: hasPermission(user, "tax_assessments", "issue"),
    adjust: hasPermission(user, "tax_assessments", "adjust"),
    cancel: hasPermission(user, "tax_assessments", "cancel"),
    collect: hasPermission(user, "tax_collection", "collect"),
    refund: hasPermission(user, "tax_collection", "refund"),
    reconcile: hasPermission(user, "tax_collection", "reconcile"),
    viewAssessments:
      hasPermission(user, "tax_assessments", "view") ||
      hasPermission(user, "taxes", "view"),
    viewConfig: hasPermission(user, "tax_configuration", "view"),
    viewCollection: hasPermission(user, "tax_collection", "view"),
    viewIssues: hasPermission(user, "user_approvals", "view"),
    manageIssues: hasPermission(user, "user_approvals", "update"),
    viewReports: hasPermission(user, "tax_reports", "view"),
    exportReports: hasPermission(user, "tax_reports", "export"),
  };

  const adminTabs = [
    ...(permissions.viewAssessments
      ? [
          ["accounts", "कर खाती"],
          ["runs", "कर आकारणी प्रक्रिया"],
          ["demands", "मागण्या"],
          ["cases", "सवलती व अपील"],
        ]
      : []),
    ...(permissions.viewConfig ? [["rules", "कर सूत्रे"]] : []),
    ...(permissions.viewCollection ? [["collections", "वसुली व पावत्या"]] : []),
    ...(permissions.reconcile ? [["reconciliation", "भरणा ताळमेळ"]] : []),
    ...(permissions.viewIssues ? [["access-issues", "जोडणी समस्या"]] : []),
    ...(permissions.viewReports ? [["reports", "अहवाल"]] : []),
  ];
  const allowedTabKeys = adminTabs.map(([key]) => key).join(",");

  useEffect(() => {
    const keys = allowedTabKeys.split(",").filter(Boolean);
    if (keys.length && !keys.includes(tab)) setTab(keys[0]);
  }, [tab, allowedTabKeys]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    const query = { limit: 100 };
    try {
      const results = await Promise.allSettled([
        permissions.viewAssessments
          ? taxEngineAPI.getAccounts(tenantSlug, query)
          : Promise.resolve({ data: {} }),
        permissions.viewConfig
          ? taxEngineAPI.getRules(tenantSlug, query)
          : Promise.resolve({ data: {} }),
        permissions.viewAssessments
          ? taxEngineAPI.getAssessmentRuns(tenantSlug, query)
          : Promise.resolve({ data: {} }),
        permissions.viewAssessments
          ? taxEngineAPI.getDemands(tenantSlug, query)
          : Promise.resolve({ data: {} }),
        permissions.viewCollection
          ? taxEngineAPI.getReceipts(tenantSlug, query)
          : Promise.resolve({ data: {} }),
        permissions.viewAssessments || permissions.viewConfig
          ? taxEngineAPI.getExemptions(tenantSlug, query)
          : Promise.resolve({ data: {} }),
        permissions.viewAssessments
          ? taxEngineAPI.getAppeals(tenantSlug, query)
          : Promise.resolve({ data: {} }),
        permissions.reconcile
          ? taxEngineAPI.getReconciliation(tenantSlug)
          : Promise.resolve({ data: {} }),
        permissions.viewIssues
          ? identityAPI.getAdminIssues(tenantSlug, { limit: 100 })
          : Promise.resolve({ data: {} }),
        permissions.viewIssues
          ? identityAPI.getAdminRegistryRequests(tenantSlug, { limit: 100 })
          : Promise.resolve({ data: {} }),
      ]);
      const attempted = results.slice(0, 7);
      if (attempted.every((item) => item.status === "rejected"))
        throw attempted[0].reason;
      setAccounts(results[0].value.data.accounts || []);
      setRules(results[1].value.data.rules || []);
      setRuns(results[2].value.data.assessment_runs || []);
      setDemands(results[3].value.data.demands || []);
      setReceipts(
        results[4].status === "fulfilled"
          ? results[4].value.data.receipts || []
          : [],
      );
      setExemptions(
        results[5].status === "fulfilled"
          ? results[5].value.data.exemptions || []
          : [],
      );
      setAppeals(
        results[6].status === "fulfilled"
          ? results[6].value.data.appeals || []
          : [],
      );
      setReconciliation(
        results[7].status === "fulfilled"
          ? results[7].value.data
          : {
              payment_intents: [],
              manual_collection_attempts: [],
              receipt_actions: [],
            },
      );
      setAccessIssues(
        results[8].status === "fulfilled"
          ? results[8].value.data.issues || []
          : [],
      );
      setRegistryRequests(
        results[9].status === "fulfilled"
          ? results[9].value.data.requests || []
          : [],
      );
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [
    tenantSlug,
    permissions.reconcile,
    permissions.viewAssessments,
    permissions.viewCollection,
    permissions.viewConfig,
    permissions.viewIssues,
  ]);
  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (tab !== "demands" || !permissions.viewAssessments) return undefined;
    let active = true;
    const timer = window.setTimeout(async () => {
      try {
        const demandStatus = [
          "draft",
          "issued",
          "partially_paid",
          "paid",
          "overdue",
          "under_appeal",
          "cancelled",
          "written_off",
          "migration_hold",
        ].includes(filters.status)
          ? filters.status
          : "";
        const response = await taxEngineAPI.getDemands(tenantSlug, {
          limit: 100,
          ...(filters.q.trim() ? { q: filters.q.trim() } : {}),
          ...(demandStatus ? { status: demandStatus } : {}),
          ...(filters.financial_year
            ? { financial_year: filters.financial_year }
            : {}),
        });
        if (active) setDemands(response.data.demands || []);
      } catch (err) {
        if (active) setError(apiErrorMessage(err));
      }
    }, 300);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [
    filters.financial_year,
    filters.q,
    filters.status,
    permissions.viewAssessments,
    tab,
    tenantSlug,
  ]);

  const filtered = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    const match = (row) =>
      (!filters.status || row.status === filters.status) &&
      (!filters.subject_type ||
        (row.subject_type || row.account?.subject_type) ===
          filters.subject_type) &&
      (!filters.financial_year ||
        row.financial_year === filters.financial_year) &&
      (!q || JSON.stringify(row).toLowerCase().includes(q));
    return {
      accounts: accounts.filter(match),
      demands: demands.filter(match),
      runs: runs.filter(match),
    };
  }, [accounts, demands, runs, filters]);
  const demandYearGroups = useMemo(
    () => groupDemandsByFinancialYear(filtered.demands),
    [filtered.demands],
  );
  const availableFinancialYears = useMemo(
    () =>
      [
        ...new Set([
          ...assessmentFinancialYears(),
          ...accounts.map((row) => row.financial_year).filter(Boolean),
          ...runs.map((row) => row.financial_year).filter(Boolean),
          ...demands.map((row) => row.financial_year).filter(Boolean),
        ]),
      ].sort((left, right) => String(right).localeCompare(String(left))),
    [accounts, demands, runs],
  );
  const demandAccountGroups = useMemo(
    () => groupDemandsByAccount(filtered.demands),
    [filtered.demands],
  );
  const demandSummary = useMemo(() => summarizeDemands(demands), [demands]);
  const overview = useMemo(
    () => ({
      activeAccounts: accounts.filter((item) => item.status === "active")
        .length,
      zeroDemand: accounts.filter(
        (account) =>
          !demands.some(
            (demand) => demand.account?.account_uid === account.account_uid,
          ),
      ).length,
      outstanding: demandSummary.outstanding_balance,
      outstandingPrincipal: demandSummary.outstanding_principal,
      outstandingPenalty: demandSummary.outstanding_penalty,
      payable: demandSummary.payable_balance,
      appealHold: demandSummary.appeal_hold_balance,
      overdue: demandSummary.overdue_balance,
    }),
    [accounts, demands, demandSummary],
  );
  const openAppeals = useMemo(
    () =>
      appeals.filter((item) =>
        ["submitted", "under_review", "hearing"].includes(item.status),
      ),
    [appeals],
  );
  const reconciliationCount = useMemo(
    () =>
      (reconciliation.manual_collection_attempts || []).length +
      (reconciliation.payment_intents || []).length +
      (reconciliation.receipt_actions || []).length,
    [reconciliation],
  );
  const openAccessIssueCount = useMemo(
    () =>
      registryRequests.filter((item) =>
        ["submitted", "under_review"].includes(item.status),
      ).length +
      accessIssues.filter((item) =>
        ["submitted", "under_review"].includes(item.status),
      ).length,
    [registryRequests, accessIssues],
  );

  const action = async (request, success) => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await request();
      setMessage(success);
      setModal(null);
      await refresh();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const provisionAllAccounts = async () => {
    setBusy(true);
    setError("");
    setMessage("");
    setAccountProvisionResult(null);
    try {
      const response = await taxEngineAPI.provisionAllAccounts(
        tenantSlug,
        { reason: "सर्व पात्र मालमत्ता व व्यवसायांसाठी कर खाती तयार केली" },
        makeIdempotencyKey("tax-account-provision"),
      );
      setAccountProvisionResult(response.data);
      const created = Number(response.data?.totals?.created || 0);
      setMessage(
        created
          ? `${created} नवीन कर खाती यशस्वीपणे तयार झाली.`
          : "सर्व पात्र नोंदींची कर खाती आधीपासून उपलब्ध आहेत.",
      );
      await refresh();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const editAccount = (event) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const account = modal.account;
    let payload;
    try {
      const parties = [];
      const partyCount = Number(form.get("party_count") || 0);
      for (let i = 0; i < partyCount; i++) {
        const partyId = form.get(`parties[${i}].party_id`);
        if (partyId) {
          parties.push({
            party_id: Number(partyId),
            role_type: form.get(`parties[${i}].role_type`) || "liable_party",
            liability_share: Number(form.get(`parties[${i}].liability_share`) || 0) || null,
            is_primary: form.get(`parties[${i}].is_primary`) === "true",
          });
        }
      }
      payload = {
        parties: parties.length > 0 ? parties : undefined,
        reason: form.get("reason"),
      };
    } catch (payloadError) {
      setError(payloadError.message);
      return undefined;
    }
    return action(
      () => taxEngineAPI.updateAccount(tenantSlug, account.account_uid, payload),
      "कर खाते अद्ययावत झाले.",
    );
  };

  const createRule = (event) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const formObj = Object.fromEntries(form);

    const rulePayload = {
      code: formObj.code,
      name: formObj.name || formObj.name_marathi,
      name_marathi: formObj.name_marathi,
      description: formObj.description || "",
      subject_type: formObj.subject_type || "property",
      account_class: formObj.account_class || "property_tax",
      billing_frequency: "annual",
      finance_category: formObj.finance_category || "tax_revenue",
      reason: formObj.reason || "नवीन कर सूत्र तयार केले",
    };

    let formulaConfig = {};
    if (formObj.formula_config) {
      try {
        formulaConfig = typeof formObj.formula_config === "string" ? JSON.parse(formObj.formula_config) : formObj.formula_config;
      } catch (e) {
        formulaConfig = { amount: 100 };
      }
    } else {
      formulaConfig = { amount: 100 };
    }

    let eligibilityConfig = {};
    if (formObj.eligibility_config) {
      try {
        eligibilityConfig = typeof formObj.eligibility_config === "string"
          ? JSON.parse(formObj.eligibility_config)
          : formObj.eligibility_config;
      } catch (e) {
        eligibilityConfig = {};
      }
    }

    const versionPayload = {
      effective_from: formObj.effective_from || "1900-01-01",
      effective_to: formObj.effective_to || null,
      formula_type: formObj.formula_type || "fixed",
      formula_config: formulaConfig,
      eligibility_config: eligibilityConfig,
      penalty_config: {},
      discount_config: {},
      exemption_config: {},
      rounding_mode: formObj.rounding_mode || "nearest_rupee",
      minimum_amount: formObj.minimum_amount || null,
      maximum_amount: formObj.maximum_amount || null,
      legal_reference: formObj.legal_reference || "महाराष्ट्र ग्रामपंचायत अधिनियम नियम २०(३)",
    };

    return action(
      async () => {
        const ruleRes = await taxEngineAPI.createRule(tenantSlug, rulePayload);
        const createdRule = ruleRes.data.rule;
        if (createdRule && createdRule.rule_uid) {
          await taxEngineAPI.createRuleVersion(tenantSlug, createdRule.rule_uid, versionPayload);
        }
        setRuleFormScreen(null);
      },
      "कर सूत्र आणि पहिली आवृत्ती मसुद्यात तयार झाली.",
    );
  };

  const updateRule = (event) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const targetRule = ruleFormScreen?.rule || modal?.rule;
    if (!targetRule?.rule_uid) return;

    return action(
      async () => {
        await taxEngineAPI.updateRule(tenantSlug, targetRule.rule_uid, {
          name: form.get("name") || form.get("name_marathi"),
          name_marathi: form.get("name_marathi"),
          description: form.get("description") || "",
          finance_category: form.get("finance_category") || "tax_revenue",
          billing_frequency: "annual",
          reason: form.get("reason") || "कर सूत्राची माहिती अद्ययावत केली",
        });
        setRuleFormScreen(null);
      },
      "कर सूत्राची माहिती अद्ययावत झाली आणि आकारणी वार्षिक निश्चित केली.",
    );
  };

  const saveVersion = (event) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const version = modal.version;
    let payload;
    try {
      payload = {
        effective_from: form.get("effective_from"),
        effective_to: form.get("effective_to") || null,
        formula_type: form.get("formula_type"),
        formula_config: json(form.get("formula_config")),
        eligibility_config: json(form.get("eligibility_config")),
        penalty_config: json(form.get("penalty_config")),
        discount_config: json(form.get("discount_config")),
        exemption_config: json(form.get("exemption_config")),
        rounding_mode: form.get("rounding_mode"),
        minimum_amount: form.get("minimum_amount") || null,
        maximum_amount: form.get("maximum_amount") || null,
        legal_reference: form.get("legal_reference"),
      };
    } catch (err) {
      setError(err.message);
      return;
    }
    return action(
      () =>
        version
          ? taxEngineAPI.updateRuleVersion(
              tenantSlug,
              version.version_uid,
              payload,
            )
          : taxEngineAPI.createRuleVersion(
              tenantSlug,
              modal.rule.rule_uid,
              payload,
            ),
      version
        ? "सूत्र आवृत्तीचा मसुदा अद्ययावत झाला."
        : "सूत्र आवृत्तीचा मसुदा तयार झाला; दुसऱ्या अधिकृत अधिकाऱ्याची मंजुरी आवश्यक आहे.",
    );
  };

  const reportParams = () =>
    Object.fromEntries(
      Object.entries(reportFilters).filter(([, value]) => value),
    );

  const reviewRuleVersion = (rule, version) => {
    const predecessor = (rule.versions || [])
      .filter((item) => (
        item.status === "approved"
        && !item.effective_to
        && version.effective_from > item.effective_from
        && rulePeriodsOverlap(item, version)
      ))
      .sort((left, right) =>
        String(right.effective_from).localeCompare(String(left.effective_from)),
      )[0];
    if (predecessor) {
      setModal({ type: "successor-approval", rule, version, predecessor });
      return;
    }
    const correctiveConflicts = (rule.versions || []).filter((item) => (
      ["approved", "superseded"].includes(item.status)
      && rulePeriodsOverlap(item, version)
    ));
    action(
      () =>
        taxEngineAPI.approveRuleVersion(tenantSlug, version.version_uid, {
          reason: correctiveConflicts.length
            ? `वार्षिक आवृत्ती ${version.version_number} मंजूर करून कर मागणी नसलेल्या ${correctiveConflicts.length} विसंगत जुन्या आवृत्त्या मागे घेतल्या`
            : "कायदेशीर संदर्भ आणि गणना संरचना तपासली",
        }),
      correctiveConflicts.length
        ? "वार्षिक सूत्र आवृत्ती मंजूर झाली आणि कर मागणी नसलेल्या विसंगत जुन्या आवृत्त्या सुरक्षितपणे मागे घेतल्या."
        : "पहिली सूत्र आवृत्ती मंजूर झाली आणि पडताळणी संकेत बंदिस्त झाला.",
    );
  };

  const approveSuccessor = (event) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    return action(
      () =>
        taxEngineAPI.approveRuleVersionSuccessor(
          tenantSlug,
          modal.version.version_uid,
          {
            predecessor_version_uid: modal.predecessor.version_uid,
            expected_predecessor_checksum: modal.predecessor.checksum,
            expected_successor_checksum: modal.version.checksum,
            reason: form.get("reason"),
          },
        ),
      "पुढील सूत्र आवृत्ती मंजूर झाली; ऐतिहासिक गणना न बदलता मागील आवृत्ती बंद झाली.",
    );
  };

  const generateAnnualRun = async (event) => {
    event.preventDefault();
    const key = makeIdempotencyKey("annual-assessment-generate");
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const response = await taxEngineAPI.generateAnnualAssessment(
        tenantSlug,
        {
          financial_year: assessmentYear,
          reason: "मंजूर वार्षिक कर सूत्रांनुसार एकत्रित कर मागण्या तयार केल्या",
        },
        key,
      );
      const run = response.data.assessment_run;
      const safelySkipped = (run.exception_report || []).filter(
        (item) => item.severity === "info",
      ).length;
      setAssessmentResult(run);
      setMessage(
        run.status === "completed"
          ? `${run.financial_year} साठी ${run.generated_count} नवीन कर मागण्या तयार व जारी झाल्या; `
            + `${safelySkipped} आधीच्या किंवा लागू नसलेल्या नोंदी सुरक्षितपणे वगळल्या`
            + `${run.error_count ? `; ${run.error_count} नोंदी दुरुस्त करून पुन्हा प्रक्रिया चालवा.` : "."}`
          : `${run.financial_year} साठी नवीन पात्र मागणी आढळली नाही; `
            + `${safelySkipped} नोंदी वगळल्या`
            + `${run.error_count ? ` आणि ${run.error_count} नोंदींमध्ये दुरुस्ती आवश्यक आहे.` : "."}`
      );
      await refresh();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const assessmentRunAction = async (request, success, runUid) => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await request();
      const response = await taxEngineAPI.getAssessmentRun(tenantSlug, runUid);
      setAssessmentResult(response.data.assessment_run);
      setMessage(success);
      await refresh();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const createAdjustment = (payloadOrEvent) => {
    let payload = payloadOrEvent;
    if (payloadOrEvent?.preventDefault) {
      payloadOrEvent.preventDefault();
      const form = new FormData(payloadOrEvent.currentTarget);
      payload = {
        adjustment_type: form.get("adjustment_type"),
        amount: form.get("amount"),
        reason_code: form.get("reason_code"),
        reason: form.get("reason"),
      };
    }
    return action(
      () =>
        taxEngineAPI.createAdjustment(tenantSlug, modal.demand.demand_uid, payload),
      "ॲडजस्टमेंट तात्काळ मंजूर करून मागणीची रक्कम अद्ययावत करण्यात आली.",
    );
  };

  const cancelDemand = (demand, payload) =>
    action(
      () => taxEngineAPI.cancelDemand(tenantSlug, demand.demand_uid, payload),
      "गणना इतिहास न बदलता मागणी रद्द झाली. सुधारित मागणीसाठी नवीन Preview तयार करा.",
    );

  const manualCollection = (payload) =>
    action(
      () =>
        taxEngineAPI.createManualCollection(
          tenantSlug,
          payload,
          makeIdempotencyKey("manual-tax"),
        ),
      ["cheque", "dd", "bank_transfer"].includes(payload.payment_mode)
        ? "भरणा दस्तऐवज स्वतंत्र पडताळणीसाठी नोंदवला. अद्याप पावती किंवा आर्थिक नोंद तयार झालेली नाही."
        : "पुष्टी झालेली वसुली अचूक करदात्याच्या नावाने नोंदवून पावती तयार झाली.",
    );

  const exemptionDecision = (item, decision) => {
    const reason = window.prompt("निर्णयाचे कारण");
    if (!reason) return;
    action(
      () =>
        taxEngineAPI.decideExemption(tenantSlug, item.exemption_uid, decision, {
          version: item.version,
          reason,
        }),
      "कर सवलतीचा निर्णय नोंदवला.",
    );
  };

  const appealProgress = (item, payload) =>
    action(
      () => taxEngineAPI.progressAppeal(tenantSlug, item.appeal_uid, payload),
      payload.status === "hearing"
        ? "अपील सुनावणी निश्चित झाली."
        : "अपीलची कागदपत्र व आकारणी तपासणी सुरू झाली.",
    );

  const openDemandDetail = async (demandUid) => {
    try {
      const response = await taxEngineAPI.getDemand(tenantSlug, demandUid);
      setModal({ type: "demand-detail", demand: response.data.demand });
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  };

  const download = async (receipt) => {
    const downloadKey = `receipt:${receipt.receipt_uid}`;
    setDocumentDownloadKey(downloadKey);
    try {
      const response = await taxEngineAPI.downloadReceipt(
        tenantSlug,
        receipt.receipt_uid,
      );
      saveBlob(response.data, `${receipt.receipt_number}.pdf`);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setDocumentDownloadKey("");
    }
  };

  const viewReceipt = async (receipt) => {
    setReceiptPreviewLoading(receipt.receipt_uid);
    setError("");
    try {
      const response = await taxEngineAPI.downloadReceipt(
        tenantSlug,
        receipt.receipt_uid,
      );
      setModal({
        type: "pdf-preview",
        blob: response.data,
        fileName: `${receipt.receipt_number || "tax-receipt"}.pdf`,
        title: `भरणा पावती ${receipt.receipt_number || ""}`.trim(),
      });
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setReceiptPreviewLoading("");
    }
  };

  const downloadConsolidatedNotice = async (group) => {
    const downloadKey = `notice:${group.account_uid}`;
    setDocumentDownloadKey(downloadKey);
    try {
      const response = await taxEngineAPI.downloadAccountDemandNotice(
        tenantSlug,
        group.account_uid,
      );
      saveBlob(
        response.data,
        `${group.account_number || "tax-account"}-consolidated-demand-notice.pdf`,
      );
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setDocumentDownloadKey("");
    }
  };

  const viewConsolidatedNotice = async (group) => {
    setDemandNoticePreviewLoading(group.account_uid);
    setError("");
    try {
      const response = await taxEngineAPI.downloadAccountDemandNotice(
        tenantSlug,
        group.account_uid,
      );
      setModal({
        type: "pdf-preview",
        blob: response.data,
        fileName: `${group.account_number || "tax-account"}-consolidated-demand-notice.pdf`,
        title: `${group.subject_name} यांचे एकत्रित मागणी बिल`,
      });
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setDemandNoticePreviewLoading("");
    }
  };

  const sendTaxReminder = async (group) => {
    const key = `reminder:${group.account_uid}`;
    setReminderSendingKey(key);
    setError("");
    try {
      const response = await taxEngineAPI.sendAccountTaxReminder(tenantSlug, group.account_uid);
      if (response.data?.sent) {
        setMessage(`${group.subject_name} यांना WhatsApp कर स्मरणपत्र पाठवले (${response.data.phone}).`);
      } else {
        setMessage(`स्मरणपत्र पाठवता आले नाही: ${response.data?.message || response.data?.reason || 'अज्ञात कारण'}`);
      }
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setReminderSendingKey("");
    }
  };
  const loadReport = async (event) => {
    event?.preventDefault();
    setBusy(true);
    setError("");
    try {
      const response = await taxEngineAPI.getReportSummary(
        tenantSlug,
        reportParams(),
      );
      setReport(response.data);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const exportReport = async () => {
    setBusy(true);
    setError("");
    try {
      const response = await taxEngineAPI.exportDemandReport(
        tenantSlug,
        reportParams(),
      );
      saveBlob(
        response.data,
        `tax-demands-${reportFilters.financial_year}.csv`,
      );
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (ruleFormScreen) {
    return (
      <section className="atx-workspace atx-form-screen-view">
        {(error || message) && (
          <div className="gp-toast-container" aria-live="polite">
            <div
              className={`gp-toast gp-toast-${error ? "error" : "success"}`}
              role={error ? "alert" : "status"}
            >
              <span className="gp-toast-icon" aria-hidden="true">
                {error ? "⚠️" : "✓"}
              </span>
              <div className="gp-toast-content">
                <div className="gp-toast-title">
                  {error ? "त्रुटी संदेश (Error)" : "यशस्वी क्रिया (Success)"}
                </div>
                <div className="gp-toast-message">{error || message}</div>
              </div>
              <button
                type="button"
                className="gp-toast-close"
                onClick={() => {
                  setError("");
                  setMessage("");
                }}
                title="संदेश बंद करा"
                aria-label="संदेश बंद करा"
              >
                ✕
              </button>
            </div>
          </div>
        )}
        <AdminTaxRuleFormScreen
          mode={ruleFormScreen.mode}
          rule={ruleFormScreen.rule}
          defaultSubjectType={
            ruleFormScreen.rule?.subject_type ||
            (ruleCategoryTab === "business" ? "business" : "property")
          }
          onCancel={() => setRuleFormScreen(null)}
          onSubmit={ruleFormScreen.mode === "edit" ? updateRule : createRule}
          busy={busy}
        />
      </section>
    );
  }

  return (
    <section className="atx-workspace">
      <header className="atx-title">
        <div>
          <span>नियंत्रित कर आकारणी व वसुली</span>
          <h1>कर व्यवस्थापन</h1>
          <p>
            मालमत्ता व व्यवसाय कर खाती, कर सूत्रे, मागण्या, वसुली आणि
            करदात्यांची प्रकरणे.
          </p>
        </div>
        <div className="atx-title-actions">
          <button
            type="button"
            className="atx-excel-import-button"
            onClick={() => setShowImportModal(true)}
            title="एक्सेल द्वारे घरे, नागरिक, मालमत्ता व कर मागण्या आयात करा"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
              color: "#ffffff",
              border: "1px solid #047857",
              padding: "0.6rem 1rem",
              borderRadius: "0.5rem",
              fontWeight: 700,
              fontSize: "0.875rem",
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(5, 150, 105, 0.25)",
              transition: "all 0.15s ease",
            }}
          >
            <span aria-hidden="true">📊</span>
            एक्सेल आयात (Excel Import)
          </button>
          <Link
            className="atx-registry-button"
            to={`/admin/${tenantSlug}/properties`}
          >
            मालमत्ता नोंदवही
            <span aria-hidden="true">→</span>
          </Link>
          <button
            className="atx-refresh-button"
            onClick={refresh}
            disabled={loading}
          >
            <span aria-hidden="true">↻</span>
            {loading ? "माहिती अद्ययावत होत आहे…" : "माहिती अद्ययावत करा"}
          </button>
        </div>
      </header>
      <TaxStatutoryDisclosure />
      {/* Floating Status Popup Notification (Instant feedback, no scrolling needed) */}
      {(error || message) && (
        <div className="gp-toast-container" aria-live="polite">
          <div
            className={`gp-toast gp-toast-${error ? "error" : "success"}`}
            role={error ? "alert" : "status"}
          >
            <span className="gp-toast-icon" aria-hidden="true">
              {error ? "⚠️" : "✓"}
            </span>
            <div className="gp-toast-content">
              <div className="gp-toast-title">
                {error ? "त्रुटी संदेश (Error)" : "यशस्वी क्रिया (Success)"}
              </div>
              <div className="gp-toast-message">{error || message}</div>
            </div>
            <button
              type="button"
              className="gp-toast-close"
              onClick={() => {
                setError("");
                setMessage("");
              }}
              title="संदेश बंद करा"
              aria-label="संदेश बंद करा"
            >
              ✕
            </button>
          </div>
        </div>
      )}
      <section className="atx-overview-panel" aria-labelledby="tax-overview-title">
        <header className="atx-overview-heading">
          <div>
            <span>आजची आर्थिक स्थिती</span>
            <h2 id="tax-overview-title">कर वसुलीचा आढावा</h2>
          </div>
          <p>सर्व सक्रिय कर खात्यांची एकत्रित रक्कम</p>
        </header>
        <div className="atx-kpis">
          <article className="atx-kpi-card accounts">
            <span className="atx-kpi-icon" aria-hidden="true">▦</span>
            <div>
              <span>सक्रिय खाती</span>
              <strong>{overview.activeAccounts}</strong>
            </div>
          </article>
          <article className="atx-kpi-card principal">
            <span className="atx-kpi-icon" aria-hidden="true">₹</span>
            <div>
              <span>मूळ कर बाकी</span>
              <strong>{money(overview.outstandingPrincipal)}</strong>
            </div>
          </article>
          <article className="atx-kpi-card penalty atx-penalty-kpi">
            <span className="atx-kpi-icon" aria-hidden="true">%</span>
            <div>
              <span>नियम २०(३) दंड</span>
              <strong>{money(overview.outstandingPenalty)}</strong>
            </div>
          </article>
          <article className="atx-kpi-card total">
            <span className="atx-kpi-icon" aria-hidden="true">Σ</span>
            <div>
              <span>दंडासह एकूण बाकी</span>
              <strong>{money(overview.outstanding)}</strong>
            </div>
          </article>
          <article className="atx-kpi-card payable">
            <span className="atx-kpi-icon" aria-hidden="true">✓</span>
            <div>
              <span>आता भरावयाची</span>
              <strong>{money(overview.payable)}</strong>
            </div>
          </article>
          <article className="atx-kpi-card appeal">
            <span className="atx-kpi-icon" aria-hidden="true">Ⅱ</span>
            <div>
              <span>अपीलमुळे स्थगित</span>
              <strong>{money(overview.appealHold)}</strong>
            </div>
          </article>
          <article className="atx-kpi-card overdue">
            <span className="atx-kpi-icon" aria-hidden="true">!</span>
            <div>
              <span>थकीत रक्कम</span>
              <strong>{money(overview.overdue)}</strong>
            </div>
          </article>
        </div>
      </section>
      <nav className="atx-tabs" aria-label="कर व्यवस्थापन विभाग">
        {adminTabs.map(([key, label]) => (
          <button
            key={key}
            className={tab === key ? "active" : ""}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </nav>
      {["accounts", "runs", "demands"].includes(tab) && (
        <div className="atx-filters">
          <label>
            शोध
            <input
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              placeholder="करदाता, मोबाइल, मागणी किंवा नोंद शोधा…"
            />
          </label>
          <label>
            स्थिती
            <select
              value={filters.status}
              onChange={(e) =>
                setFilters({ ...filters, status: e.target.value })
              }
            >
              <option value="">सर्व</option>
              {[
                "draft",
                "active",
                "issued",
                "partially_paid",
                "paid",
                "overdue",
                "under_appeal",
                "preview_ready",
                "approved",
                "completed",
              ].map((value) => (
                <option key={value} value={value}>
                  {statusLabel(value)}
                </option>
              ))}
            </select>
          </label>
          <label>
            कर विषय
            <select
              value={filters.subject_type}
              onChange={(e) =>
                setFilters({ ...filters, subject_type: e.target.value })
              }
            >
              <option value="">सर्व</option>
              <option value="property">मालमत्ता</option>
              <option value="business">व्यवसाय</option>
              <option value="service_connection">सेवा जोडणी</option>
            </select>
          </label>
          <label>
            आर्थिक वर्ष
            <select
              value={filters.financial_year}
              onChange={(e) =>
                setFilters({ ...filters, financial_year: e.target.value })
              }
            >
              <option value="">सर्व आर्थिक वर्षे</option>
              {availableFinancialYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
      {loading && <div className="atx-loading">कर माहिती लोड होत आहे…</div>}

      {!loading && tab === "accounts" && (
        <>
          <div className="atx-section-head">
            <div>
              <h2>कर खाती</h2>
              <p>
                मागणी नसलेली सक्रिय खातीही येथे दिसतात; त्यामुळे करपात्र
                मालमत्ता किंवा व्यवसाय नोंद लपणार नाही.
              </p>
            </div>
            {permissions.generate && (
              <button
                className="atx-primary"
                onClick={provisionAllAccounts}
                disabled={busy}
              >
                {busy ? "कर खाती तयार होत आहेत…" : "+ सर्व कर खाती तयार करा"}
              </button>
            )}
          </div>
          <AccountProvisionResult
            result={accountProvisionResult}
            onClose={() => setAccountProvisionResult(null)}
          />
          <div
            className="atx-table-wrap atx-data-grid-scroll"
            role="region"
            aria-label="कर खाती"
            tabIndex="0"
          >
            <table className="atx-data-grid-table atx-account-grid">
              <colgroup>
                <col className="atx-col-account" />
                <col className="atx-col-subject" />
                <col className="atx-col-account-class" />
                <col className="atx-col-grid-status" />
                <col className="atx-col-parties" />
                <col className="atx-col-grid-actions" />
              </colgroup>
              <thead>
                <tr>
                  <th>खाते</th>
                  <th>मालमत्ता / व्यवसाय</th>
                  <th>कर प्रकार</th>
                  <th>स्थिती</th>
                  <th>संबंधित करदाते</th>
                  <th>कृती</th>
                </tr>
              </thead>
              <tbody>
                {filtered.accounts.map((account) => {
                  const subject = accountSubjectDisplay(account);
                  return (
                    <tr key={account.account_uid}>
                      <td>
                        <strong
                          className="atx-grid-ellipsis"
                          title={account.account_number}
                        >
                          {account.account_number}
                        </strong>
                      </td>
                      <td>
                        <strong
                          className="atx-grid-ellipsis"
                          title={subject.name}
                        >
                          {subject.name}
                        </strong>
                        {subject.detail && (
                          <small
                            className="atx-grid-ellipsis"
                            title={subject.detail}
                          >
                            {subject.detail}
                          </small>
                        )}
                      </td>
                      <td>{accountClassLabel(account.account_class)}</td>
                      <td>
                        <Status value={account.status} />
                      </td>
                      <td>
                        {(() => {
                          const taxpayerNames =
                            account.parties
                              ?.map(
                                (item) =>
                                  item.legal_name ||
                                  item.display_name ||
                                  item.role_type,
                              )
                              .join(", ") ||
                            "सक्रियतेवेळी करदाता जोडला जाईल";
                          return (
                            <span
                              className="atx-grid-ellipsis"
                              title={taxpayerNames}
                            >
                              {taxpayerNames}
                            </span>
                          );
                        })()}
                      </td>
                      <td>
                        <div className="atx-actions">
                          {account.status === "draft" &&
                            permissions.configure && (
                              <button
                                onClick={() =>
                                  setModal({ type: "edit-account", account })
                                }
                              >
                                संपादित करा
                              </button>
                            )}
                          {account.status === "draft" &&
                            permissions.activateAccount && (
                              <button
                                onClick={() =>
                                  action(
                                    () =>
                                      taxEngineAPI.activateAccount(
                                        tenantSlug,
                                        account.account_uid,
                                        { reason: "नोंदवही व करदाते पडताळले" },
                                      ),
                                    "कर खाते सक्रिय झाले आणि प्रवेश अधिकार जोडले गेले.",
                                  )
                                }
                              >
                                सक्रिय करा
                              </button>
                            )}
                          {["draft", "active", "suspended"].includes(
                            account.status,
                          ) &&
                            permissions.closeAccount && (
                              <button
                                onClick={() => {
                                  const reason = window.prompt(
                                    "खाते बंद करण्याचे कारण (किमान ५ अक्षरे)",
                                  );
                                  if (reason)
                                    action(
                                      () =>
                                        taxEngineAPI.closeAccount(
                                          tenantSlug,
                                          account.account_uid,
                                          { reason },
                                        ),
                                      "कर खाते बंद झाले.",
                                    );
                                }}
                              >
                                बंद करा
                              </button>
                            )}
                          {permissions.configure && (
                            <button
                              onClick={() =>
                                setModal({ type: "exemption", account })
                              }
                            >
                              सवलत
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!filtered.accounts.length && (
                  <tr>
                    <td colSpan="6" className="atx-empty">
                      या निकषांशी जुळणारी कर खाती नाहीत.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!loading && tab === "rules" && (() => {
        const propertyRuleCount = rules.filter(
          (r) => r.subject_type === "property" || r.account_class === "property_tax",
        ).length;
        const businessRuleCount = rules.filter(
          (r) => r.subject_type === "business" || r.account_class === "business_tax",
        ).length;
        const otherRuleCount = rules.length - propertyRuleCount - businessRuleCount;

        const filteredRules = rules.filter((rule) => {
          if (ruleCategoryTab === "property") {
            return rule.subject_type === "property" || rule.account_class === "property_tax";
          }
          if (ruleCategoryTab === "business") {
            return rule.subject_type === "business" || rule.account_class === "business_tax";
          }
          if (ruleCategoryTab === "other") {
            return (
              rule.subject_type !== "property" &&
              rule.subject_type !== "business" &&
              rule.account_class !== "property_tax" &&
              rule.account_class !== "business_tax"
            );
          }
          return true;
        });

        const renderVersionCard = (rule, version, isPrimary = false) => {
          const formulaSummary = formatFormulaConfigSummary(version);
          return (
            <div
              className={`atx-version-card ${version.status} ${isPrimary ? "primary-active" : "historical"}`}
              key={version.version_uid}
            >
              <div className="atx-version-main">
                <div className="atx-version-badge-row">
                  <span className="atx-version-num">
                    {isPrimary ? "⭐ सक्रिय आवृत्ती " : "आवृत्ती "}
                    {version.version_number}
                  </span>
                  <Status value={version.status} />
                  <span className="atx-formula-type-tag">
                    {formulaSummary.icon} {formulaTypeLabel(version.formula_type)}
                  </span>
                </div>

                <div className="atx-formula-highlight-box">
                  <div className="atx-formula-title">{formulaSummary.title}</div>
                  <div className="atx-formula-detail">{formulaSummary.detail}</div>
                  {formulaSummary.formattedSlabs && formulaSummary.formattedSlabs.length > 0 && (
                    <div className="atx-slab-preview-list">
                      {formulaSummary.formattedSlabs.map((item, i) => (
                        <span key={i} className="atx-slab-chip">
                          {item.limitLabel}: <strong>{item.rateLabel}</strong>
                        </span>
                      ))}
                    </div>
                  )}
                  {formulaSummary.classRates && formulaSummary.classRates.length > 0 && (
                    <div className="atx-class-rate-grid">
                      {formulaSummary.classRates.map((item, i) => (
                        <span key={i} className="atx-class-rate-chip">
                          <strong>{item.label}:</strong> {item.rate}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="atx-version-meta-row">
                  <span>
                    📅 लागू: {shortDate(version.effective_from)} ते{" "}
                    {version.effective_to ? shortDate(version.effective_to) : "सध्या कार्यरत"}
                  </span>
                  <span>⚖️ संदर्भ: {version.legal_reference || "नियम २०(३)"}</span>
                  <span>
                    🔢 पूर्णांकन:{" "}
                    {version.rounding_mode === "nearest_rupee"
                      ? "जवळच्या रुपयात"
                      : version.rounding_mode}
                  </span>
                  {version.minimum_amount && <span>⬇️ किमान ₹{version.minimum_amount}</span>}
                  {version.maximum_amount && <span>⬆️ कमाल ₹{version.maximum_amount}</span>}
                </div>
              </div>

              <div className="atx-version-actions">
                {permissions.configure && (
                  <button
                    className="atx-btn-edit-version"
                    onClick={() => setModal({ type: "version", rule, version })}
                    title={
                      version.status === "approved"
                        ? "सुधारित मसुदा आवृत्ती तयार करण्यासाठी संपादित करा"
                        : "मसुदा संपादित करा"
                    }
                  >
                    ✏️ {version.status === "approved" ? "सुधारित आवृत्ती करा" : "संपादित करा"}
                  </button>
                )}

                {version.status === "draft" && permissions.approveConfig && (
                  <button
                    className="atx-btn-approve-version"
                    onClick={() => reviewRuleVersion(rule, version)}
                  >
                    {(rule.versions || []).some(
                      (item) =>
                        ["approved", "superseded"].includes(item.status) &&
                        rulePeriodsOverlap(item, version),
                    )
                      ? "✓ वार्षिक आवृत्तीने दुरुस्त करा"
                      : (rule.versions || []).some(
                            (item) => item.status === "approved" && !item.effective_to,
                          )
                        ? "✓ पुढील आवृत्ती मंजूर करा"
                        : "✓ पहिली आवृत्ती मंजूर करा"}
                  </button>
                )}
              </div>
            </div>
          );
        };


        return (
          <>
            <div className="atx-section-head">
              <div>
                <h2>कर सूत्रे आणि त्यांच्या आकारणी आवृत्त्या (Tax Rules & Versions)</h2>
                <p>
                  मालमत्ता व व्यवसाय कर आकारणी पद्धती, दर व कायदेशीर संदर्भ येथे श्रेणीनुसार व्यवस्थापित करा.
                </p>
              </div>
              {permissions.configure && (
                <button
                  className="atx-primary"
                  onClick={() => setRuleFormScreen({ mode: "create", rule: null })}
                >
                  + कर सूत्र तयार करा
                </button>
              )}
            </div>

            {/* Category Filter Tabs: All | Property | Business | Other */}
            <nav className="atx-rule-category-nav" aria-label="कर सूत्र विभाग निवडा">
              <button
                type="button"
                className={`atx-rule-cat-pill ${ruleCategoryTab === "all" ? "active" : ""}`}
                onClick={() => setRuleCategoryTab("all")}
              >
                <span>सर्व कर सूत्रे</span>
                <span className="atx-cat-badge">{rules.length}</span>
              </button>
              <button
                type="button"
                className={`atx-rule-cat-pill ${ruleCategoryTab === "property" ? "active" : ""}`}
                onClick={() => setRuleCategoryTab("property")}
              >
                <span aria-hidden="true">🏠</span>
                <span>मालमत्ता कर (घरपट्टी)</span>
                <span className="atx-cat-badge">{propertyRuleCount}</span>
              </button>
              <button
                type="button"
                className={`atx-rule-cat-pill ${ruleCategoryTab === "business" ? "active" : ""}`}
                onClick={() => setRuleCategoryTab("business")}
              >
                <span aria-hidden="true">🏢</span>
                <span>व्यवसाय कर</span>
                <span className="atx-cat-badge">{businessRuleCount}</span>
              </button>
              {otherRuleCount > 0 && (
                <button
                  type="button"
                  className={`atx-rule-cat-pill ${ruleCategoryTab === "other" ? "active" : ""}`}
                  onClick={() => setRuleCategoryTab("other")}
                >
                  <span aria-hidden="true">💧</span>
                  <span>पाणीपट्टी व इतर</span>
                  <span className="atx-cat-badge">{otherRuleCount}</span>
                </button>
              )}
            </nav>

            <div className="atx-rule-grid">
              {filteredRules.length === 0 ? (
                <div className="atx-empty-state">
                  <p>या श्रेणीमध्ये सध्या कोणतेही कर सूत्र उपलब्ध नाही.</p>
                </div>
              ) : (
                filteredRules.map((rule) => {
                  const ruleIcon =
                    rule.subject_type === "property"
                      ? "🏠"
                      : rule.subject_type === "business"
                      ? "🏢"
                      : rule.subject_type === "service_connection"
                      ? "💧"
                      : "📜";

                  const versions = Array.isArray(rule.versions) ? [...rule.versions] : [];
                  const now = new Date();
                  const approvedActive = versions.find(
                    (v) =>
                      v.status === "approved" &&
                      (!v.effective_to || new Date(v.effective_to) >= now),
                  );
                  const anyApproved = versions.find((v) => v.status === "approved");
                  const activeVersion = approvedActive || anyApproved || versions[0];
                  const otherVersions = versions.filter(
                    (v) => v.version_uid !== activeVersion?.version_uid,
                  );
                  const pendingDraft = otherVersions.find((v) => v.status === "draft");
                  const isExpanded = Boolean(expandedRuleVersions[rule.rule_uid]);

                  return (
                    <article key={rule.rule_uid} className="atx-rule-card">
                      <header className="atx-rule-header">
                        <div className="atx-rule-title-box">
                          <span className="atx-rule-type-icon" aria-hidden="true">
                            {ruleIcon}
                          </span>
                          <Status value={rule.status} />
                          <h3>{rule.name_marathi || rule.name}</h3>
                          <span className="atx-rule-code">{rule.code}</span>
                        </div>
                        {permissions.configure && (
                          <div className="atx-rule-header-actions">
                            <button
                              className="atx-btn-edit-rule"
                              onClick={() => setRuleFormScreen({ mode: "edit", rule })}
                            >
                              ✏️ सूत्र माहिती संपादित करा
                            </button>
                            <button
                              className="atx-btn-add-version"
                              onClick={() => setModal({ type: "version", rule })}
                            >
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                              >
                                <path d="M12 5v14M5 12h14" />
                              </svg>
                              <span>नवीन आवृत्ती</span>
                            </button>
                          </div>
                        )}
                      </header>

                      {rule.description && <p className="atx-rule-desc">{rule.description}</p>}

                      <div className="atx-rule-meta-bar">
                        <div className="atx-meta-item">
                          <span>कर विषय</span>
                          <strong>{subjectTypeLabel(rule.subject_type)}</strong>
                        </div>
                        <div className="atx-meta-item">
                          <span>कर प्रकार</span>
                          <strong>{accountClassLabel(rule.account_class)}</strong>
                        </div>
                        <div className="atx-meta-item">
                          <span>आकारणी वारंवारता</span>
                          <strong>{frequencyLabel(rule.billing_frequency)}</strong>
                        </div>
                        <div className="atx-meta-item">
                          <span>एकूण आवृत्त्या</span>
                          <strong>{versions.length} आवृत्ती</strong>
                        </div>
                      </div>

                      <div className="atx-versions-container">
                        <div className="atx-versions-header-row">
                          <h4 className="atx-versions-heading">कर आकारणी सूत्र रचना</h4>
                          {otherVersions.length > 0 && (
                            <span className="atx-version-count-tag">
                              १ सक्रिय + {otherVersions.length} मागील
                            </span>
                          )}
                        </div>

                        {!activeVersion && (
                          <div className="atx-empty-version">
                            <span>या सूत्रासाठी अद्याप कोणतीही आवृत्ती तयार केलेली नाही.</span>
                          </div>
                        )}

                        {pendingDraft && (
                          <div className="atx-pending-draft-alert">
                            <div className="atx-pending-draft-info">
                              <strong>🔔 नवीन मसुदा आवृत्ती {pendingDraft.version_number} उपलब्ध आहे</strong>
                              <span>
                                {" "}(लागू: {shortDate(pendingDraft.effective_from)}) - मंजुरी प्रलंबित
                              </span>
                            </div>
                            <button
                              type="button"
                              className="atx-btn-review-draft"
                              onClick={() => {
                                setExpandedRuleVersions((prev) => ({
                                  ...prev,
                                  [rule.rule_uid]: true,
                                }));
                                reviewRuleVersion(rule, pendingDraft);
                              }}
                            >
                              तपासा व मंजूर करा →
                            </button>
                          </div>
                        )}

                        {/* Primary Active Version Card */}
                        {activeVersion && renderVersionCard(rule, activeVersion, true)}

                        {/* Collapsible Section for Historical / Other Versions */}
                        {otherVersions.length > 0 && (
                          <div className="atx-other-versions-section">
                            <div className="atx-other-versions-toggle-bar">
                              <button
                                type="button"
                                className={`atx-btn-toggle-versions ${isExpanded ? "expanded" : ""}`}
                                onClick={() => toggleRuleVersions(rule.rule_uid)}
                                aria-expanded={isExpanded}
                              >
                                <span>📜 मागील व इतर आवृत्त्या ({otherVersions.length})</span>
                                <span className="atx-toggle-chevron">
                                  {isExpanded ? "▲ लपवा" : "▼ पहा"}
                                </span>
                              </button>
                            </div>
                            {isExpanded && (
                              <div className="atx-other-versions-list">
                                {otherVersions.map((version) =>
                                  renderVersionCard(rule, version, false),
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </article>
                  );
                })
              )}
            </div>
          </>
        );
      })()}

      {!loading && tab === "runs" && (
        <>
          {permissions.issue && (
            <form className="atx-annual-workbench" onSubmit={generateAnnualRun}>
              <div className="atx-annual-workbench-copy">
                <h3>वार्षिक कर मागण्या</h3>
                <p>आधीच्या मागण्या वगळून पात्र नोंदींसाठी कर तयार होतील.</p>
              </div>
              <label>
                {/* आर्थिक वर्ष */}
                <select
                  required
                  value={assessmentYear}
                  onChange={(event) => setAssessmentYear(event.target.value)}
                >
                  {assessmentFinancialYears().map((year) => (
                    <option key={year} value={year}>
                      आर्थिक वर्ष {year}
                    </option>
                  ))}
                </select>
              </label>
              <button
                className="atx-primary atx-generate-demands-button"
                disabled={busy}
              >
                {busy ? "कर मागण्या तयार होत आहेत…" : "कर मागण्या तयार करा"}
              </button>
            </form>
          )}

          <AnnualAssessmentResult
            run={assessmentResult}
            accounts={accounts}
            rules={rules}
            busy={busy}
            permissions={permissions}
            tenantSlug={tenantSlug}
            onClear={() => setAssessmentResult(null)}
            onApprove={() =>
              assessmentRunAction(
                () =>
                  taxEngineAPI.approveAssessmentRun(
                    tenantSlug,
                    assessmentResult.run_uid,
                    { reason: "Preview मधील एकूण कर व दुरुस्ती अहवाल तपासला" },
                  ),
                "कर आकारणी प्रक्रिया मंजूर झाली.",
                assessmentResult.run_uid,
              )
            }
            onIssue={() =>
              assessmentRunAction(
                () =>
                  taxEngineAPI.issueAssessmentRun(
                    tenantSlug,
                    assessmentResult.run_uid,
                    { reason: "मंजूर आकारणी प्रक्रिया जारी केली" },
                  ),
                "कर मागण्या जारी झाल्या.",
                assessmentResult.run_uid,
              )
            }
          />

          <div className="atx-subsection-head">
            <div>
              <h3>मागील कर आकारणी प्रक्रिया</h3>
              <p>तपासणी, मंजुरी आणि जारी केलेल्या प्रक्रियांचा इतिहास</p>
            </div>
          </div>
          <div
            className="atx-table-wrap atx-data-grid-scroll"
            role="region"
            aria-label="मागील कर आकारणी प्रक्रिया"
            tabIndex="0"
          >
            <table className="atx-data-grid-table atx-run-grid">
              <colgroup>
                <col className="atx-col-run-type" />
                <col className="atx-col-run-year" />
                <col className="atx-col-run-status" />
                <col className="atx-col-run-records" />
                <col className="atx-col-run-total" />
                <col className="atx-col-run-actions" />
              </colgroup>
              <thead>
                <tr>
                  <th>प्रक्रिया</th>
                  <th>आर्थिक वर्ष</th>
                  <th>स्थिती</th>
                  <th>नोंदी</th>
                  <th>तपासणीतील एकूण कर</th>
                  <th>कृती</th>
                </tr>
              </thead>
              <tbody>
                {filtered.runs.map((run) => (
                  <tr key={run.run_uid}>
                    <td>
                      <strong>{runTypeLabel(run.run_type)}</strong>
                    </td>
                    <td>{run.financial_year}</td>
                    <td>
                      <Status value={run.status} />
                    </td>
                    <td>
                      {run.generated_count} नवीन ·{" "}
                      {(run.exception_report || []).filter(
                        (item) => item.severity === "info",
                      ).length || Math.max(0, Number(run.skipped_count) - Number(run.error_count))}{" "}
                      वगळलेली
                      {run.error_count > 0 && ` · ${run.error_count} त्रुटी`}
                    </td>
                    <td>{money(run.preview_total)}</td>
                    <td>
                      <div className="atx-actions">
                        <button
                          onClick={async () => {
                            try {
                              const r = await taxEngineAPI.getAssessmentRun(
                                tenantSlug,
                                run.run_uid,
                              );
                              setAssessmentResult(r.data.assessment_run);
                              window.requestAnimationFrame(() => {
                                document
                                  .querySelector(".atx-assessment-result")
                                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
                              });
                            } catch (e) {
                              setError(apiErrorMessage(e));
                            }
                          }}
                        >
                          तपासा
                        </button>
                        {run.status === "preview_ready" &&
                          Number(run.generated_count) > 0 &&
                          permissions.approve && (
                            <button
                              onClick={() =>
                                assessmentRunAction(
                                  () =>
                                    taxEngineAPI.approveAssessmentRun(
                                      tenantSlug,
                                      run.run_uid,
                                      {
                                        reason:
                                          "Preview मधील एकूण कर व अपवाद अहवाल तपासला",
                                      },
                                    ),
                                  "कर आकारणी प्रक्रिया मंजूर झाली.",
                                  run.run_uid,
                                )
                              }
                            >
                              मंजूर करा
                            </button>
                          )}
                        {run.status === "approved" && permissions.issue && (
                            <button
                              onClick={() =>
                                assessmentRunAction(
                                  () =>
                                    taxEngineAPI.issueAssessmentRun(
                                      tenantSlug,
                                    run.run_uid,
                                    {
                                      reason:
                                        "मंजूर आकारणी प्रक्रिया जारी केली",
                                    },
                                    ),
                                  "कर मागण्या जारी झाल्या.",
                                  run.run_uid,
                                )
                              }
                          >
                            मागण्या जारी करा
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!loading && tab === "demands" && (
        <>
          <div className="atx-section-head">
            <div>
              <h2>कर मागण्या</h2>
              <p>आर्थिक वर्षानुसार कर, भरलेली रक्कम आणि बाकी.</p>
            </div>
          </div>
          <div className="atx-demand-year-list">
            {demandYearGroups.map(([financialYear, yearDemands]) => {
              const yearSummary = summarizeDemands(yearDemands);
              return (
                <section className="atx-demand-year-group" key={financialYear}>
                  <header>
                    <strong>आर्थिक वर्ष {financialYear}</strong>
                    <span>{yearDemands.length} कर मागण्या</span>
                    <span>
                      दंडासह बाकी{" "}
                      <b>{money(yearSummary.outstanding_balance)}</b>
                    </span>
                    <span>
                      त्यातील दंड{" "}
                      <b>{money(yearSummary.outstanding_penalty)}</b>
                    </span>
                  </header>
                  <div
                    className="atx-table-wrap atx-demand-scroll"
                    role="region"
                    aria-label={`आर्थिक वर्ष ${financialYear} कर मागण्या`}
                    tabIndex="0"
                  >
                    <table className="atx-demand-table">
                      <colgroup>
                        <col className="atx-col-tax" />
                        <col className="atx-col-taxpayer" />
                        <col className="atx-col-period" />
                        <col className="atx-col-status" />
                        <col className="atx-col-money" />
                        <col className="atx-col-money" />
                        <col className="atx-col-money" />
                        <col className="atx-col-actions" />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>कर मागणी</th>
                          <th>करदाता व नोंद</th>
                          <th>कालावधी</th>
                          <th>स्थिती</th>
                          <th>आकारणी</th>
                          <th>भरले</th>
                          <th>बाकी</th>
                          <th>कृती</th>
                        </tr>
                      </thead>
                      <tbody>
                        {yearDemands.map((demand) => (
                      <tr className="atx-demand-row" key={demand.demand_uid}>
                        <td className="atx-demand-tax-cell">
                          <strong
                            className="atx-grid-ellipsis"
                            title={demandTaxName(demand)}
                          >
                            {demandTaxName(demand)}
                          </strong>
                          <div className="atx-demand-flags">
                            {Number(demand.penalty_amount) > 0 && (
                              <small className="atx-grid-flag penalty">
                                नियम २०(३) दंड
                              </small>
                            )}
                            {demand.display?.tax_name_needs_review && (
                              <small className="atx-grid-flag review">
                                करनाव तपासा
                              </small>
                            )}
                            {demand.has_pending_adjustment && (
                              <small className="atx-grid-flag warning" style={{ backgroundColor: '#fff3cd', color: '#856404', borderColor: '#ffeeba' }}>
                                ॲडजस्टमेंट प्रलंबित
                              </small>
                            )}
                          </div>
                        </td>
                        <td className="atx-demand-subject-cell">
                          <span
                            className="atx-taxpayer-name atx-grid-ellipsis"
                            title={demand.account?.taxpayer?.name || ""}
                          >
                            {demand.account?.taxpayer?.name ||
                              "करदाता नोंद उपलब्ध नाही"}
                          </span>
                          <strong
                            className="atx-grid-ellipsis"
                            title={demandSubjectName(demand)}
                          >
                            {demandSubjectName(demand)}
                          </strong>
                          {demandSubjectReference(demand) &&
                            !/^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(
                              demandSubjectReference(demand),
                            ) && (
                              <small
                                className="atx-grid-ellipsis"
                                title={demandSubjectReference(demand)}
                              >
                                {demandSubjectReference(demand)}
                              </small>
                            )}
                        </td>
                        <td className="atx-demand-period-cell">
                          {shortDate(demand.period_start)} –{" "}
                          {shortDate(demand.period_end)}
                          <small>देय: {shortDate(demand.due_date)}</small>
                        </td>
                        <td>
                          <Status value={demand.status} />
                          {demand.has_pending_adjustment && (
                            <div style={{ marginTop: '4px' }}>
                              <small className="atx-grid-flag warning" style={{ backgroundColor: '#fff3cd', color: '#856404', borderColor: '#ffeeba', display: 'inline-block' }}>
                                ॲडजस्टमेंट प्रलंबित
                              </small>
                            </div>
                          )}
                        </td>
                        <td className="atx-demand-money-cell">
                          <strong>{money(demand.total_amount)}</strong>
                          {Number(demandAmountBreakdown(demand).penalty) > 0 && (
                            <small className="atx-penalty-text">
                              दंड {money(demandAmountBreakdown(demand).penalty)}
                            </small>
                          )}
                        </td>
                        <td className="atx-demand-money-cell">
                          <strong>{money(demand.paid_amount)}</strong>
                          <DemandPaymentHistory demand={demand} compact />
                        </td>
                        <td className="atx-demand-money-cell">
                          <strong>{money(demandBalance(demand))}</strong>
                          {Number(
                            demandAmountBreakdown(demand).outstandingPenalty,
                          ) > 0 && (
                            <small className="atx-penalty-text">
                              दंड{" "}
                              {money(
                                demandAmountBreakdown(demand)
                                  .outstandingPenalty,
                              )}
                            </small>
                          )}
                        </td>
                        <td>
                          <div className="atx-actions">
                            <button
                              className="atx-grid-action view"
                              type="button"
                              title="मागणीचा तपशील पहा"
                              aria-label="मागणीचा तपशील पहा"
                              onClick={async () => {
                                try {
                                  const r = await taxEngineAPI.getDemand(
                                    tenantSlug,
                                    demand.demand_uid,
                                  );
                                  setModal({
                                    type: "demand-detail",
                                    demand: r.data.demand,
                                  });
                                } catch (e) {
                                  setError(apiErrorMessage(e));
                                }
                              }}
                            >
                              <GridActionIcon name="view" />
                            </button>
                            {demand.has_pending_adjustment && demand.pending_adjustments?.[0] && permissions.approve && (
                              <button
                                className="atx-grid-action approve"
                                type="button"
                                style={{ color: "#166534", borderColor: "#86efac", backgroundColor: "#f0fdf4" }}
                                title={`प्रलंबित ॲडजस्टमेंट (${money(demand.pending_adjustments[0].amount)}) तात्काळ मंजूर करा`}
                                aria-label="प्रलंबित ॲडजस्टमेंट मंजूर करा"
                                onClick={() =>
                                  action(
                                    () =>
                                      taxEngineAPI.approveAdjustment(
                                        tenantSlug,
                                        demand.pending_adjustments[0].adjustment_uid,
                                        {
                                          reason: "ॲडजस्टमेंटचा पुरावा आणि मागणीची बाकी पडताळली",
                                        },
                                      ),
                                    "ॲडजस्टमेंट तात्काळ मंजूर झाले आणि मागणी पुन्हा मोजली गेली.",
                                  )
                                }
                              >
                                <GridActionIcon name="approve" />
                              </button>
                            )}
                            {permissions.adjust &&
                              [
                                "issued",
                                "partially_paid",
                                "paid",
                                "overdue",
                                "under_appeal",
                              ].includes(demand.status) && (
                                <button
                                  className="atx-grid-action adjust"
                                  type="button"
                                  title="मागणीचे ॲडजस्टमेंट करा"
                                  aria-label="मागणीचे ॲडजस्टमेंट करा"
                                  onClick={() =>
                                    setModal({ type: "adjustment", demand })
                                  }
                                >
                                  <GridActionIcon name="adjust" />
                                </button>
                              )}
                            {permissions.collect &&
                              demandBalance(demand) > 0 &&
                              ["issued", "partially_paid", "overdue"].includes(
                                demand.status,
                              ) && (
                                <button
                                  className="atx-grid-action payment"
                                  type="button"
                                  title="भरणा नोंदवा"
                                  aria-label="भरणा नोंदवा"
                                  onClick={() =>
                                    setModal({ type: "manual", demand })
                                  }
                                >
                                  <GridActionIcon name="payment" />
                                </button>
                              )}
                            {canCancelDemand(demand, permissions.cancel) && (
                              <button
                                className="atx-grid-action cancel"
                                type="button"
                                title="न भरलेली मागणी रद्द करा"
                                aria-label={`${demandTaxName(demand)} मागणी रद्द करा`}
                                onClick={() =>
                                  setModal({ type: "cancel-demand", demand })
                                }
                              >
                                <GridActionIcon name="cancel" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              );
            })}
          </div>
        </>
      )}

      {!loading && tab === "collections" && (
        <>
          <div className="atx-subview-pills" role="tablist" aria-label="वसुली उपविभाग">
            <button
              type="button"
              role="tab"
              aria-selected={collectionsSubView === "bills"}
              className={`atx-pill-btn ${collectionsSubView === "bills" ? "active" : ""}`}
              onClick={() => setCollectionsSubView("bills")}
            >
              <span className="atx-pill-badge">{demandAccountGroups.length}</span>
              एकत्रित मागणी बिले
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={collectionsSubView === "receipts"}
              className={`atx-pill-btn ${collectionsSubView === "receipts" ? "active" : ""}`}
              onClick={() => setCollectionsSubView("receipts")}
            >
              <span className="atx-pill-badge">{receipts.length}</span>
              भरणा पावत्या आणि परतावे
            </button>
          </div>

          {collectionsSubView === "bills" && (
            <>
              <div className="atx-section-head">
                <div>
                  <h2>एकत्रित मागणी बिले</h2>
                  <p>
                    प्रत्येक कर खात्यासाठी सर्व कर आणि सर्व आर्थिक वर्षे एका बिलात
                    दाखवली आहेत. येथे आपण थेट कर भरणा नोंदवू शकता किंवा स्मरणपत्र पाठवू शकता.
                  </p>
                </div>
              </div>
              {!demandAccountGroups.length && (
                <div className="atx-empty">एकत्रित मागणी बिल उपलब्ध नाही.</div>
              )}
              {!!demandAccountGroups.length && (
                <div
                  className="atx-table-wrap atx-bill-grid-scroll"
                  tabIndex="0"
                  role="region"
                  aria-label="एकत्रित मागणी बिलांची यादी"
                >
                  <table className="atx-data-grid-table atx-bill-grid">
                    <colgroup>
                      <col className="atx-col-bill-subject" />
                      <col className="atx-col-bill-years" />
                      <col className="atx-col-bill-count" />
                      <col className="atx-col-bill-assessment" />
                      <col className="atx-col-bill-paid" />
                      <col className="atx-col-bill-balance" />
                      <col className="atx-col-bill-actions" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th scope="col">मालमत्ता / व्यवसाय</th>
                        <th scope="col">आर्थिक वर्षे</th>
                        <th scope="col">मागण्या</th>
                        <th scope="col">आकारणी</th>
                        <th scope="col">भरले</th>
                        <th scope="col">दंडासह बाकी</th>
                        <th scope="col">कृती</th>
                      </tr>
                    </thead>
                    <tbody>
                      {demandAccountGroups.map((group) => {
                        const subjectType =
                          group.demands?.[0]?.account?.subject_type;
                        return (
                          <tr key={`notice-${group.account_uid}`}>
                            <td>
                              <span className={`atx-subject-kind ${subjectType || ""}`}>
                                {subjectTypeLabel(subjectType)}
                              </span>
                              <strong className="atx-bill-subject-name">
                                {group.subject_name}
                              </strong>
                              {group.subject_reference && (
                                <small>{group.subject_reference}</small>
                              )}
                            </td>
                            <td>
                              <div className="atx-year-chips">
                                {group.financial_years.map((year) => (
                                  <span key={year}>{year}</span>
                                ))}
                              </div>
                            </td>
                            <td>
                              <strong>{group.demands.length}</strong>
                              <small>कर मागण्या</small>
                            </td>
                            <td>
                              <strong>{money(group.total_amount)}</strong>
                              <small>
                                मूळ {money(group.principal_amount)} · दंड{" "}
                                {money(group.penalty_amount)}
                              </small>
                            </td>
                            <td>
                              <strong className="atx-paid-amount">
                                {money(group.paid_amount)}
                              </strong>
                            </td>
                            <td>
                              <strong className="atx-balance-amount">
                                {money(group.balance_amount)}
                              </strong>
                              <small>
                                त्यातील दंड {money(group.outstanding_penalty)}
                              </small>
                            </td>
                            <td>
                              <div className="atx-actions atx-bill-actions">
                                <button
                                  className="atx-grid-action view"
                                  type="button"
                                  disabled={demandNoticePreviewLoading === group.account_uid}
                                  title={
                                    demandNoticePreviewLoading === group.account_uid
                                      ? "बिल उघडत आहे…"
                                      : "एकत्रित मागणी बिल पहा"
                                  }
                                  aria-label={`${group.subject_name} यांचे एकत्रित मागणी बिल पहा`}
                                  onClick={() => viewConsolidatedNotice(group)}
                                >
                                  {demandNoticePreviewLoading === group.account_uid
                                    ? <ActionSpinner />
                                    : <GridActionIcon name="view" />}
                                </button>
                                <button
                                  className="atx-grid-action download"
                                  type="button"
                                  disabled={documentDownloadKey === `notice:${group.account_uid}`}
                                  title={
                                    documentDownloadKey === `notice:${group.account_uid}`
                                      ? "PDF डाउनलोड होत आहे…"
                                      : "PDF डाउनलोड करा"
                                  }
                                  aria-label={`${group.subject_name} यांचे मागणी बिल PDF डाउनलोड करा`}
                                  onClick={() => downloadConsolidatedNotice(group)}
                                >
                                  {documentDownloadKey === `notice:${group.account_uid}`
                                    ? <ActionSpinner />
                                    : <GridActionIcon name="download" />}
                                </button>
                                {permissions.collect && Number(group.balance_amount) > 0 && (
                                  <button
                                    id={`btn-collect-bill-${group.account_uid}`}
                                    className="atx-grid-action payment"
                                    type="button"
                                    title="कर भरणा नोंदवा"
                                    aria-label={`${group.subject_name} यांचा कर भरणा नोंदवा`}
                                    onClick={() => {
                                      const payableDemand = (group.demands || []).find(
                                        (d) => demandBalance(d) > 0 && ["issued", "partially_paid", "overdue"].includes(d.status)
                                      ) || group.demands?.[0];
                                      if (payableDemand) {
                                        setModal({ type: "manual", demand: payableDemand });
                                      }
                                    }}
                                  >
                                    <GridActionIcon name="payment" />
                                  </button>
                                )}
                                {permissions.collect && (
                                  <button
                                    id={`btn-send-tax-reminder-${group.account_uid}`}
                                    className="atx-grid-action whatsapp"
                                    type="button"
                                    disabled={reminderSendingKey === `reminder:${group.account_uid}` || !group.balance_amount}
                                    title={
                                      !group.balance_amount
                                        ? "थकबाकी नाही"
                                        : reminderSendingKey === `reminder:${group.account_uid}`
                                        ? "स्मरणपत्र पाठवत आहे…"
                                        : "WhatsApp कर स्मरणपत्र पाठवा"
                                    }
                                    aria-label={`${group.subject_name} यांना WhatsApp कर स्मरणपत्र पाठवा`}
                                    onClick={() => sendTaxReminder(group)}
                                  >
                                    {reminderSendingKey === `reminder:${group.account_uid}`
                                      ? <ActionSpinner />
                                      : <GridActionIcon name="whatsapp" />}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {collectionsSubView === "receipts" && (
            <>
              <div className="atx-section-head">
                <div>
                  <h2>भरणा पावत्या आणि परतावे</h2>
                  <p>
                    फक्त पडताळलेल्या भरण्यानंतर पावती तयार होते; एका व्यवहारातील
                    सर्व निवडलेल्या कर मागण्या एकाच पावतीत एकत्र राहतात.
                  </p>
                </div>
              </div>
              {!receipts.length && (
                <div className="atx-empty">अद्याप भरणा पावती नाही.</div>
              )}
              {!!receipts.length && (
                <div
                  className="atx-table-wrap atx-receipt-grid-scroll"
                  tabIndex="0"
                  role="region"
                  aria-label="भरणा पावत्या आणि परतावे"
                >
                  <table className="atx-data-grid-table atx-receipt-grid">
                    <colgroup>
                      <col className="atx-col-receipt" />
                      <col className="atx-col-receipt-payer" />
                      <col className="atx-col-receipt-payment" />
                      <col className="atx-col-receipt-demands" />
                      <col className="atx-col-receipt-amount" />
                      <col className="atx-col-receipt-actions" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th scope="col">पावती</th>
                        <th scope="col">करदाता</th>
                        <th scope="col">भरणा तपशील</th>
                        <th scope="col">समाविष्ट मागण्या</th>
                        <th scope="col">भरलेली रक्कम</th>
                        <th scope="col">कृती</th>
                      </tr>
                    </thead>
                    <tbody>
                      {receipts.map((receipt) => (
                        <tr key={receipt.receipt_uid}>
                          <td>
                            <strong>{receipt.receipt_number}</strong>
                            <small><Status value={receipt.status} /></small>
                          </td>
                          <td>
                            <strong>{receipt.payer?.legal_name || "पोर्टल करदाता"}</strong>
                            {receipt.payer?.phone && <small>{receipt.payer.phone}</small>}
                          </td>
                          <td>
                            <strong>{shortDate(receipt.paid_at)}</strong>
                            <small>{paymentModeLabel(receipt.payment_mode)}</small>
                            {receipt.provider === "razorpay" &&
                              receipt.provider_payment_id && (
                                <small>
                                  व्यवहार क्र. <code>{receipt.provider_payment_id}</code>
                                </small>
                              )}
                            {receipt.tender_evidence?.clearance_reference && (
                              <small>
                                पडताळणी संदर्भ: {receipt.tender_evidence.clearance_reference}
                              </small>
                            )}
                          </td>
                          <td>
                            <strong>{receipt.allocations?.length || 0}</strong>
                            <small>कर मागण्या</small>
                          </td>
                          <td>
                            <strong className="atx-paid-amount">{money(receipt.amount)}</strong>
                          </td>
                          <td>
                            <div className="atx-actions atx-receipt-actions">
                              <button
                                className="atx-grid-action view"
                                type="button"
                                disabled={receiptPreviewLoading === receipt.receipt_uid}
                                title={
                                  receiptPreviewLoading === receipt.receipt_uid
                                    ? "पावती उघडत आहे…"
                                    : "पावती पहा"
                                }
                                aria-label={`${receipt.receipt_number} पावती पहा`}
                                onClick={() => viewReceipt(receipt)}
                              >
                                {receiptPreviewLoading === receipt.receipt_uid
                                  ? <ActionSpinner />
                                  : <GridActionIcon name="view" />}
                              </button>
                              <button
                                className="atx-grid-action download"
                                type="button"
                                disabled={documentDownloadKey === `receipt:${receipt.receipt_uid}`}
                                title={
                                  documentDownloadKey === `receipt:${receipt.receipt_uid}`
                                    ? "PDF डाउनलोड होत आहे…"
                                    : "PDF डाउनलोड करा"
                                }
                                aria-label={`${receipt.receipt_number} पावती PDF डाउनलोड करा`}
                                onClick={() => download(receipt)}
                              >
                                {documentDownloadKey === `receipt:${receipt.receipt_uid}`
                                  ? <ActionSpinner />
                                  : <GridActionIcon name="download" />}
                              </button>
                              {receipt.status === "paid" && permissions.refund && (
                                <button
                                  className="atx-grid-action refund"
                                  type="button"
                                  title="परतावा किंवा उलट नोंद"
                                  aria-label={`${receipt.receipt_number} परतावा किंवा उलट नोंद`}
                                  onClick={() =>
                                    setModal({ type: "receipt-action", receipt })
                                  }
                                >
                                  <GridActionIcon name="refund" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}

      {!loading && tab === "cases" && (
        <>
          <div className="atx-section-head">
            <div>
              <h2>कर सवलती व अपील प्रकरणे</h2>
              <p>
                अर्ज स्वीकारणे, पुरावे तपासणे, सुनावणी निश्चित करणे आणि
                कारणासह अंतिम निर्णय नोंदवणे ही संपूर्ण प्रक्रिया येथे हाताळा.
              </p>
            </div>
          </div>
          <div className="atx-workflow-kpis">
            <article>
              <span>प्रलंबित सवलती</span>
              <strong>{exemptions.filter((item) => item.status === "pending").length}</strong>
            </article>
            <article>
              <span>खुली अपील</span>
              <strong>{openAppeals.length}</strong>
            </article>
            <article>
              <span>सुनावणीवर</span>
              <strong>{appeals.filter((item) => item.status === "hearing").length}</strong>
            </article>
            <article>
              <span>निकाली अपील</span>
              <strong>{appeals.length - openAppeals.length}</strong>
            </article>
          </div>
          <div className="atx-case-grid atx-case-management-grid">
          <section>
            <header className="atx-queue-heading">
              <div>
                <span>सवलत पडताळणी</span>
                <h2>कर सवलती</h2>
              </div>
              <b>{exemptions.length}</b>
            </header>
            {exemptions.map((item) => (
              <article key={item.exemption_uid}>
                <div>
                  <Status value={item.status} />
                  <h3>{item.exemption_type}</h3>
                  <p>
                    {item.account?.account_number} ·{" "}
                    {item.percentage
                      ? `${item.percentage}%`
                      : money(item.fixed_amount)}
                  </p>
                  <small>{item.reason}</small>
                </div>
                <div className="atx-actions">
                  {item.status === "pending" && permissions.approveConfig && (
                    <>
                      <button
                        onClick={() => exemptionDecision(item, "approve")}
                      >
                        मंजूर करा
                      </button>
                      <button onClick={() => exemptionDecision(item, "reject")}>
                        नाकारा
                      </button>
                    </>
                  )}
                  {item.status === "approved" && permissions.approveConfig && (
                    <button onClick={() => exemptionDecision(item, "revoke")}>
                      मंजुरी मागे घ्या
                    </button>
                  )}
                </div>
              </article>
            ))}
            {!exemptions.length && (
              <div className="atx-empty atx-compact-empty">
                सध्या कर सवलतीचा कोणताही अर्ज नाही.
              </div>
            )}
          </section>
          <section>
            <header className="atx-queue-heading">
              <div>
                <span>मागणी पुनर्तपासणी</span>
                <h2>अपील</h2>
              </div>
              <b>{appeals.length}</b>
            </header>
            {appeals.map((item) => (
              <article className="atx-appeal-card" key={item.appeal_uid}>
                <div className="atx-appeal-main">
                  <div className="atx-appeal-status-row">
                    <Status value={item.status} />
                    {item.stay_granted && ["submitted", "under_review", "hearing"].includes(item.status) && (
                      <span className="atx-stay-badge">वसुली स्थगित</span>
                    )}
                  </div>
                  <h3>मागणी क्र. {item.demand?.demand_number}</h3>
                  <small>
                    {item.demand?.account?.account_number
                      ? `कर खाते ${item.demand.account.account_number} · `
                      : ""}
                    अर्जदार {item.filed_by?.full_name || "नोंदणीकृत करदाता"}
                    {item.filed_by?.phone ? ` · ${item.filed_by.phone}` : ""}
                  </small>
                  <strong className="atx-appeal-reason">
                    {APPEAL_REASON_LABELS[item.reason_code] ||
                      item.reason_code?.replaceAll("_", " ") ||
                      "कर आकारणीविषयी हरकत"}
                  </strong>
                  <p>{item.statement}</p>
                  <div className="atx-appeal-finance">
                    <span>एकूण <b>{money(item.demand?.total_amount)}</b></span>
                    <span>भरले <b>{money(item.demand?.paid_amount)}</b></span>
                    <span>बाकी <b>{money(item.demand?.balance_amount)}</b></span>
                  </div>
                  <small>
                    अपील दिनांक {shortDate(item.created_at)}
                    {item.assigned_to?.full_name
                      ? ` · तपास अधिकारी ${item.assigned_to.full_name}`
                      : ""}
                    {item.hearing_at
                      ? ` · सुनावणी ${new Date(item.hearing_at).toLocaleString("mr-IN")}`
                      : ""}
                  </small>
                  {item.decision && (
                    <p className="atx-case-decision">
                      <strong>अंतिम निर्णय:</strong> {item.decision}
                    </p>
                  )}
                  {item.has_resolution_adjustment && (
                    <small className="atx-inline-warning">
                      निर्णयाशी संबंधित ॲडजस्टमेंट मागणी तपशीलातून स्वतंत्रपणे मंजूर करणे आवश्यक आहे.
                    </small>
                  )}
                </div>
                <div className="atx-actions">
                  <button
                    type="button"
                    onClick={() => openDemandDetail(item.demand?.demand_uid)}
                  >
                    मागणी व ॲडजस्टमेंट पहा
                  </button>
                  {["submitted", "under_review", "hearing"].includes(item.status) &&
                    permissions.approve && (
                      <>
                        <button
                          type="button"
                          onClick={() =>
                            setModal({
                              type: "appeal-progress",
                              appeal: item,
                              targetStatus: "under_review",
                            })
                          }
                        >
                          {item.status === "submitted"
                            ? "तपासणी सुरू करा"
                            : "तपासणी नोंदवा"}
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setModal({
                              type: "appeal-progress",
                              appeal: item,
                              targetStatus: "hearing",
                            })
                          }
                        >
                          {item.status === "hearing"
                            ? "सुनावणी बदला"
                            : "सुनावणी निश्चित करा"}
                        </button>
                        <button
                          type="button"
                          className="atx-case-primary-action"
                          onClick={() =>
                            setModal({ type: "appeal-decision", appeal: item })
                          }
                        >
                          निर्णय द्या
                        </button>
                      </>
                    )}
                </div>
              </article>
            ))}
            {!appeals.length && (
              <div className="atx-empty atx-compact-empty">
                सध्या कोणतेही अपील प्रकरण उपलब्ध नाही.
              </div>
            )}
          </section>
          </div>
        </>
      )}

      {!loading && tab === "reconciliation" && (
        <>
          <div className="atx-section-head">
            <div>
              <h2>भरणा ताळमेळ रांग</h2>
              <p>
                धनादेश व बँक हस्तांतरण स्वतंत्र पडताळणी होईपर्यंत खात्यात जमा
                होत नाहीत. ऑनलाइन भरण्यातील अनिश्चितता मूळ भरणा विनंतीशी पडताळली
              जाते.
              </p>
            </div>
          </div>
          <div className="atx-workflow-note">
            <div>
              <span>ताळमेळाची गरज कधी पडते?</span>
              <strong>
                धनादेश/बँक जमा पडताळणी, अडकलेला Razorpay व्यवहार किंवा
                परतावा/उलट नोंदीचा अनिश्चित निकाल
              </strong>
            </div>
            <b>{reconciliationCount} प्रलंबित</b>
          </div>
          {!permissions.reconcile ? (
            <div className="atx-empty">
              तुमच्याकडे भरणा ताळमेळ परवानगी नाही.
            </div>
          ) : (
            <div className="atx-case-grid atx-reconciliation-grid">
              <section>
                <header className="atx-queue-heading">
                  <div><span>Maker-checker पडताळणी</span><h2>कार्यालयीन भरणा दस्तऐवज</h2></div>
                  <b>{(reconciliation.manual_collection_attempts || []).length}</b>
                </header>
                {(reconciliation.manual_collection_attempts || []).map(
                  (item) => (
                    <article key={item.attempt_uid}>
                      <div>
                        <Status value={item.status} />
                        <h3>
                          {paymentModeLabel(item.payment_mode)} ·{" "}
                          {money(item.amount)}
                        </h3>
                        <p>
                          {item.payer?.legal_name ||
                            "करदात्याची नोंद उपलब्ध नाही"}{" "}
                          · {item.external_reference}
                        </p>
                        <small>
                          सादर दिनांक {shortDate(item.submitted_at)} · आवृत्ती{" "}
                          {item.version}
                        </small>
                        {!item.can_review && (
                          <small className="atx-inline-error">
                            हा दस्तऐवज दुसऱ्या अधिकाऱ्याने तपासणे आवश्यक आहे.
                          </small>
                        )}
                      </div>
                      <button
                        disabled={!item.can_review}
                        onClick={() =>
                          setModal({ type: "manual-clearance", attempt: item })
                        }
                      >
                        जमा पडताळा
                      </button>
                    </article>
                  ),
                )}
                {!reconciliation.manual_collection_attempts?.length && (
                  <div className="atx-empty atx-compact-empty">
                    पडताळणीसाठी धनादेश, DD किंवा बँक हस्तांतरण प्रलंबित नाही.
                  </div>
                )}
              </section>
              <section>
                <header className="atx-queue-heading">
                  <div><span>Provider पडताळणी</span><h2>ऑनलाइन भरणा विनंत्या</h2></div>
                  <b>{(reconciliation.payment_intents || []).length}</b>
                </header>
                {(reconciliation.payment_intents || []).map((item) => (
                  <article key={item.intent_uid}>
                    <div>
                      <Status value={item.status} />
                      <h3>{item.intent_uid}</h3>
                      <p>
                        {money(item.amount)} ·{" "}
                        {item.provider_order_id ||
                          "प्रदात्याचा क्रमांक प्रलंबित"}
                      </p>
                      <small>{item.failure_reason}</small>
                    </div>
                    <button
                      onClick={() =>
                        setModal({ type: "reconcile-intent", intent: item })
                      }
                    >
                      निकाल नोंदवा
                    </button>
                  </article>
                ))}
                {!reconciliation.payment_intents?.length && (
                  <div className="atx-empty atx-compact-empty">
                    Razorpay किंवा ऑनलाइन भरण्याचा अनिश्चित व्यवहार नाही.
                  </div>
                )}
              </section>
              <section>
                <header className="atx-queue-heading">
                  <div><span>परतावा व उलट नोंद</span><h2>पावतीवरील कृती</h2></div>
                  <b>{(reconciliation.receipt_actions || []).length}</b>
                </header>
                {(reconciliation.receipt_actions || []).map((item) => (
                  <article key={item.action_uid}>
                    <div>
                      <h3>
                        {item.action_type} · {money(item.amount)}
                      </h3>
                      <small>{item.failure_reason}</small>
                    </div>
                    <button
                      onClick={() =>
                        setModal({ type: "reconcile-action", action: item })
                      }
                    >
                      निकाल नोंदवा
                    </button>
                  </article>
                ))}
                {!reconciliation.receipt_actions?.length && (
                  <div className="atx-empty atx-compact-empty">
                    पडताळणी आवश्यक असलेला परतावा किंवा उलट नोंद नाही.
                  </div>
                )}
              </section>
            </div>
          )}
        </>
      )}

      {!loading && tab === "access-issues" && (
        <>
          <div className="atx-section-head">
            <div>
              <h2>नोंदणी सहाय्य आणि खाते प्रवेश समस्या</h2>
              <p>
                नागरिकाचा अर्ज अधिकृत नोंद थेट बदलत नाही. कागदपत्रे, register आणि
                आवश्यक असल्यास स्थळ पाहणी करून master record स्वतंत्रपणे अद्ययावत करा;
                त्यानंतरच येथे कार्यालयीन निर्णय नोंदवा.
              </p>
            </div>
            <span className="atx-section-count">{openAccessIssueCount} खुली प्रकरणे</span>
          </div>
          <div className="atx-workflow-note">
            <div>
              <span>ही स्क्रीन कशासाठी आहे?</span>
              <strong>
                नवीन नोंद/दुरुस्ती अर्ज आणि चुकीच्या कर खाते प्रवेशाची तक्रार
                तपासून अधिकृत नोंद व access grant सुसंगत ठेवण्यासाठी
              </strong>
            </div>
            <b>{openAccessIssueCount} प्रलंबित</b>
          </div>
          <div className="atx-list">
            {registryRequests.map((request) => (
              <article key={request.request_uid}>
                <div>
                  <Status value={request.status} />
                  <h3>{REGISTRY_REQUEST_LABELS[request.request_type] || "नोंदणी सहाय्य अर्ज"}</h3>
                  <p>{request.reference_number || "संदर्भ क्रमांक दिलेला नाही"}</p>
                  <small>{request.statement}</small>
                  {request.applicant && <small>
                    अर्जदार: {request.applicant.full_name}
                    {request.applicant.phone ? ` · ${request.applicant.phone}` : ""}
                    {request.applicant.email ? ` · ${request.applicant.email}` : ""}
                  </small>}
                  {request.resolution && <p><strong>कार्यालयीन निर्णय:</strong> {request.resolution}</p>}
                </div>
                <div className="atx-actions">
                  {request.status === "submitted" && permissions.manageIssues && (
                    <button onClick={() => {
                      const resolution = window.prompt("प्राथमिक तपासणीची नोंद");
                      if (resolution) action(
                        () => identityAPI.reviewRegistryRequest(tenantSlug, request.request_uid, {
                          version: request.version, status: "under_review", resolution,
                        }),
                        "अर्ज तपासणीसाठी स्वीकारला.",
                      );
                    }}>तपासणी सुरू करा</button>
                  )}
                  {["submitted", "under_review"].includes(request.status) && permissions.manageIssues && (
                    <>
                      <button onClick={() => {
                        const resolution = window.prompt("नोंद अद्ययावत केल्याचा संदर्भ/ठराव आणि निर्णय लिहा");
                        if (resolution) action(
                          () => identityAPI.decideRegistryRequest(tenantSlug, request.request_uid, {
                            version: request.version, status: "resolved", resolution,
                          }),
                          "नोंदणी सहाय्य अर्ज निकाली काढला.",
                        );
                      }}>निकाली काढा</button>
                      <button onClick={() => {
                        const resolution = window.prompt("अर्ज नामंजूर करण्याचे कारण");
                        if (resolution) action(
                          () => identityAPI.decideRegistryRequest(tenantSlug, request.request_uid, {
                            version: request.version, status: "rejected", resolution,
                          }),
                          "नोंदणी सहाय्य अर्ज नामंजूर केला.",
                        );
                      }}>नामंजूर करा</button>
                    </>
                  )}
                </div>
              </article>
            ))}
            {!registryRequests.length && <div className="atx-empty">प्रलंबित नोंदणी सहाय्य अर्ज नाही.</div>}
          </div>
          <div className="atx-section-head atx-subsection-head"><div><h2>चुकीची खाते जोडणी</h2><p>फक्त विद्यमान access grant संबंधी तक्रारी.</p></div></div>
          <div className="atx-list">
            {accessIssues.map((issue) => (
              <article key={issue.issue_uid}>
                <div>
                  <Status value={issue.status} />
                  <h3>
                    {issue.issue_type?.replaceAll("_", " ")} ·{" "}
                    {subjectTypeLabel(issue.target_type)}
                  </h3>
                  <p>{issue.target_uid}</p>
                  <small>{issue.statement}</small>
                  {issue.resolution && (
                    <p>
                      <strong>निकाल:</strong> {issue.resolution}
                    </p>
                  )}
                </div>
                <div className="atx-actions">
                  {issue.status === "submitted" && permissions.manageIssues && (
                    <button
                      onClick={() => {
                        const resolution = window.prompt("प्राथमिक तपासणी टीप");
                        if (resolution)
                          action(
                            () =>
                              identityAPI.reviewIssue(
                                tenantSlug,
                                issue.issue_uid,
                                {
                                  version: issue.version,
                                  status: "under_review",
                                  resolution,
                                },
                              ),
                            "समस्या तपासणीसाठी नेमली गेली.",
                          );
                      }}
                    >
                      तपासणी सुरू करा
                    </button>
                  )}
                  {["submitted", "under_review"].includes(issue.status) &&
                    permissions.manageIssues && (
                      <button
                        onClick={() =>
                          setModal({ type: "issue-decision", issue })
                        }
                      >
                        निर्णय द्या
                      </button>
                    )}
                </div>
              </article>
            ))}
            {!accessIssues.length && (
              <div className="atx-empty">
                या ग्रामपंचायतीत खाते जोडणीची समस्या नाही.
              </div>
            )}
          </div>
        </>
      )}

      {!loading && tab === "reports" && (
        <>
          <div className="atx-section-head">
            <div>
              <h2>कर अहवाल</h2>
              <p>
                आर्थिक वर्षाची एकूण रक्कम, थकबाकी कालावधी, प्रभाग आणि व्यवसाय
                वर्गवारीनुसार तपशील.
              </p>
            </div>
            {report && permissions.exportReports && (
              <button
                className="atx-primary"
                disabled={busy}
                onClick={exportReport}
              >
                मागणी CSV निर्यात करा
              </button>
            )}
          </div>
          <form className="atx-filters" onSubmit={loadReport}>
            <label>
              आर्थिक वर्ष
              <input
                required
                value={reportFilters.financial_year}
                onChange={(e) =>
                  setReportFilters({
                    ...reportFilters,
                    financial_year: e.target.value,
                  })
                }
              />
            </label>
            <label>
              या दिनांकापर्यंत
              <input
                type="date"
                value={reportFilters.as_of}
                onChange={(e) =>
                  setReportFilters({ ...reportFilters, as_of: e.target.value })
                }
              />
            </label>
            <label>
              कर विषय
              <select
                value={reportFilters.subject_type}
                onChange={(e) =>
                  setReportFilters({
                    ...reportFilters,
                    subject_type: e.target.value,
                    account_class:
                      e.target.value === "business"
                        ? "business_tax"
                        : e.target.value === "property"
                          ? "property_tax"
                          : e.target.value === "service_connection"
                            ? "service_charge"
                            : "",
                  })
                }
              >
                <option value="">सर्व</option>
                <option value="property">मालमत्ता</option>
                <option value="business">व्यवसाय</option>
                <option value="service_connection">सेवा जोडणी</option>
              </select>
            </label>
            <label>
              कर प्रकार
              <select
                value={reportFilters.account_class}
                onChange={(e) =>
                  setReportFilters({
                    ...reportFilters,
                    account_class: e.target.value,
                  })
                }
              >
                <option value="">सर्व</option>
                <option value="property_tax">मालमत्ता कर</option>
                <option value="business_tax">व्यवसाय कर</option>
                <option value="service_charge">सेवा कर</option>
              </select>
            </label>
            <label>
              प्रभाग
              <input
                value={reportFilters.ward_number}
                onChange={(e) =>
                  setReportFilters({
                    ...reportFilters,
                    ward_number: e.target.value,
                  })
                }
              />
            </label>
            <label>
              व्यवसाय वर्गवारी
              <input
                value={reportFilters.business_category}
                onChange={(e) =>
                  setReportFilters({
                    ...reportFilters,
                    business_category: e.target.value,
                  })
                }
              />
            </label>
            <button className="atx-primary" disabled={busy}>
              अहवाल तयार करा
            </button>
          </form>
          {report && (
            <div className="atx-report">
              <div className="atx-kpis">
                <article>
                  <span>खाती</span>
                  <strong>{report.totals?.account_count || 0}</strong>
                </article>
                <article>
                  <span>एकूण मागणी</span>
                  <strong>{money(report.totals?.total_demanded)}</strong>
                </article>
                <article>
                  <span>वसुली</span>
                  <strong>
                    {money(report.totals?.total_paid)}{" "}
                    <small>
                      {report.totals?.collection_rate_percent || 0}%
                    </small>
                  </strong>
                </article>
                <article>
                  <span>बाकी</span>
                  <strong>{money(report.totals?.outstanding_balance)}</strong>
                </article>
              </div>
              <div className="atx-case-grid">
                <section>
                  <h2>थकबाकी कालावधी</h2>
                  {Object.entries(report.aging || {}).map(([key, value]) => (
                    <div className="atx-line" key={key}>
                      <span>{REPORT_AGING_LABELS[key] || key.replaceAll("_", " ")}</span>
                      <strong>{money(value)}</strong>
                    </div>
                  ))}
                </section>
                <section>
                  <h2>ताळमेळ</h2>
                  <div className="atx-line">
                    <span>मागण्यांवरील भरलेली रक्कम</span>
                    <strong>
                      {money(report.reconciliation?.demand_paid_total)}
                    </strong>
                  </div>
                  <div className="atx-line">
                    <span>सक्रिय वाटप</span>
                    <strong>
                      {money(report.reconciliation?.active_allocation_total)}
                    </strong>
                  </div>
                  <div className="atx-line">
                    <span>तफावत</span>
                    <strong>{money(report.reconciliation?.variance)}</strong>
                  </div>
                </section>
                {[
                  ["ward", "प्रभाग"],
                  ["business_category", "व्यवसाय वर्गवारी"],
                  ["subject_type", "कर विषय"],
                  ["account_class", "कर प्रकार"],
                ].map(([key, label]) => (
                  <section key={key}>
                    <h2>{label}</h2>
                    {(report.groups?.[key] || []).map((group) => (
                      <div className="atx-line" key={group.key}>
                        <span>
                          {reportGroupLabel(key, group.key)}
                          <small>
                            {group.account_count} खाती ·{" "}
                            {group.collection_rate_percent}% वसुली
                          </small>
                        </span>
                        <strong>{money(group.outstanding_balance)}</strong>
                      </div>
                    ))}
                  </section>
                ))}
              </div>
            </div>
          )}
          {!report && (
            <div className="atx-report-empty">
              <span>अहवाल तयार करण्यासाठी वरील निकष निवडा</span>
              <h3>आकारणी, वसुली, थकबाकी, दंड आणि ताळमेळ एकाच अहवालात</h3>
              <p>
                आर्थिक वर्षानुसार एकूण मागणी व वसुली, प्रभाग/व्यवसाय वर्गवारी,
                थकबाकीचे वय, खुली अपील आणि लेखा तफावत तपासता येईल. आवश्यक असल्यास
                त्याच निकषांचा CSV निर्यात अहवाल डाउनलोड करता येतो.
              </p>
            </div>
          )}
        </>
      )}

      {modal?.type === "pdf-preview" && (
        <TaxPdfViewer
          blob={modal.blob}
          fileName={modal.fileName}
          title={modal.title}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === "cancel-demand" && (
        <Modal title="कर मागणी रद्द करा" onClose={() => setModal(null)} error={error}>
          <form
            className="atx-form"
            onSubmit={(event) => {
              event.preventDefault();
              const form = new FormData(event.currentTarget);
              cancelDemand(
                modal.demand,
                demandCancellationPayload(
                  modal.demand,
                  form.get("reason"),
                ),
              );
            }}
          >
            <section className="atx-cancel-demand-context">
              <div>
                <span>कर</span>
                <strong>{demandTaxName(modal.demand)}</strong>
              </div>
              <div>
                <span>करदाता</span>
                <strong>
                  {modal.demand.account?.taxpayer?.name ||
                    "करदाता नोंद उपलब्ध नाही"}
                </strong>
              </div>
              <div>
                <span>मालमत्ता / व्यवसाय</span>
                <strong>{demandSubjectName(modal.demand)}</strong>
              </div>
              <div>
                <span>आर्थिक वर्ष</span>
                <strong>{modal.demand.financial_year}</strong>
              </div>
              <div>
                <span>आकारणी</span>
                <strong>{money(modal.demand.total_amount)}</strong>
              </div>
              <div>
                <span>बाकी</span>
                <strong>{money(demandBalance(modal.demand))}</strong>
              </div>
            </section>
            <label>
              मागणी रद्द करण्याचे सविस्तर कारण
              <textarea
                name="reason"
                required
                minLength="10"
                maxLength="2000"
                placeholder="दुबार मागणी, आकारणीतील चूक किंवा कायदेशीर त्रुटीचे तपासलेले कारण नमूद करा."
              />
            </label>
            <p className="atx-warning">
              ही कृती मागणीचा गणना इतिहास पुसणार नाही. सुधारित करासाठी नवीन
              आकारणी प्रक्रिया चालवावी लागेल.
            </p>
            <footer>
              <button type="button" onClick={() => setModal(null)}>
                मागे जा
              </button>
              <button className="atx-danger" disabled={busy}>
                मागणी रद्द करा
              </button>
            </footer>
          </form>
        </Modal>
      )}
      {modal?.type === "edit-account" && (
        <Modal title="कर खाते संपादित करा" onClose={() => setModal(null)} error={error}>
          <form className="atx-form" onSubmit={editAccount}>
            <input type="hidden" name="party_count" value={modal.account.parties?.length || 0} />
            <div>
              <h3>करदाते</h3>
              {(modal.account.parties || []).map((party, idx) => (
                <div key={idx} className="atx-party-row">
                  <input type="hidden" name={`parties[${idx}].party_id`} value={party.party_id} />
                  <label>
                    <span>{party.legal_name || party.display_name}</span>
                    <input
                      name={`parties[${idx}].role_type`}
                      type="hidden"
                      value={party.role_type}
                    />
                    <select
                      defaultValue={party.role_type}
                      onChange={(e) => {
                        document.querySelector(`input[name="parties[${idx}].role_type"]`).value = e.target.value;
                      }}
                    >
                      <option value="liable_party">दायित्वाचा पक्ष</option>
                      <option value="co_liable_party">संयुक्त दायित्वाचा पक्ष</option>
                      <option value="authorized_representative">अधिकृत प्रतिनिधी</option>
                    </select>
                  </label>
                  {party.role_type === "co_liable_party" && (
                    <label>
                      <span>दायित्व शेअर (%)</span>
                      <input
                        type="number"
                        name={`parties[${idx}].liability_share`}
                        min="0"
                        max="100"
                        step="0.01"
                        defaultValue={party.liability_share || 0}
                      />
                    </label>
                  )}
                  <label className="atx-checkbox">
                    <input
                      type="checkbox"
                      name={`parties[${idx}].is_primary`}
                      defaultChecked={party.is_primary}
                      onChange={(e) => {
                        document.querySelector(`input[name="parties[${idx}].is_primary"]`).value = e.target.checked ? "true" : "false";
                      }}
                    />
                    <span>प्राथमिक करदाता</span>
                  </label>
                  <input type="hidden" name={`parties[${idx}].is_primary`} value={party.is_primary ? "true" : "false"} />
                </div>
              ))}
            </div>
            <label>
              संपादनाचे कारण
              <textarea name="reason" required minLength="5" />
            </label>
            <footer>
              <button type="button" onClick={() => setModal(null)}>
                रद्द करा
              </button>
              <button className="atx-primary" disabled={busy}>
                अद्ययावत करा
              </button>
            </footer>
          </form>
        </Modal>
      )}

      {modal?.type === "version" && (
        <Modal
          title={`${modal.rule.name_marathi || modal.rule.code} सूत्राची ${modal.version ? "आवृत्ती संपादित करा" : "नवीन आवृत्ती तयार करा"}`}
          onClose={() => setModal(null)}
          error={error}
        >
          <form className="atx-form" onSubmit={saveVersion}>
            <div className="atx-form-section">
              <h4 className="atx-form-section-title">१. लागू कालावधी व वैधानिक संदर्भ (Effective Period)</h4>
              <div className="atx-form-row">
                <label>
                  पासून लागू (Effective From) *
                  <input
                    name="effective_from"
                    type="date"
                    required
                    defaultValue={modal.version?.effective_from}
                  />
                </label>
                <label>
                  पर्यंत लागू (Effective To - ऐच्छिक)
                  <input
                    name="effective_to"
                    type="date"
                    defaultValue={modal.version?.effective_to}
                  />
                </label>
              </div>
              <div className="atx-form-row">
                <label>
                  पूर्णांकन पद्धत (Rounding Mode)
                  <select
                    name="rounding_mode"
                    defaultValue={modal.version?.rounding_mode || "nearest_rupee"}
                  >
                    <option value="nearest_rupee">जवळच्या रुपयात (Nearest Rupee)</option>
                    <option value="up_rupee">पुढील रुपयात (Round Up)</option>
                    <option value="down_rupee">मागील रुपयात (Round Down)</option>
                    <option value="none">पूर्णांकन नाही (Exact)</option>
                  </select>
                </label>
                <label>
                  कायदेशीर संदर्भ *
                  <input
                    name="legal_reference"
                    required
                    defaultValue={modal.version?.legal_reference || "महाराष्ट्र ग्रामपंचायत अधिनियम नियम २०(३)"}
                  />
                </label>
              </div>
            </div>

            <div className="atx-form-section">
              <h4 className="atx-form-section-title">२. दर व आकारणी सूत्र रचना (Pricing Rule & Formula Config)</h4>
              <TaxPricingRuleEditor
                key={modal.version?.version_uid || `new-ver-${modal.rule.rule_uid}`}
                initialType={modal.version?.formula_type || "fixed"}
                initialConfig={modal.version?.formula_config}
                subjectType={modal.rule?.subject_type || "property"}
              />
            </div>

            <div className="atx-form-section">
              <h4 className="atx-form-section-title">३. आकारणी मर्यादा (Minimum / Maximum Limits)</h4>
              <div className="atx-form-row">
                <label>
                  किमान रक्कम (Minimum Amount ₹)
                  <input
                    name="minimum_amount"
                    type="number"
                    min="0"
                    step="0.01"
                    defaultValue={modal.version?.minimum_amount}
                    placeholder="उदा. 50"
                  />
                </label>
                <label>
                  कमाल रक्कम (Maximum Amount ₹)
                  <input
                    name="maximum_amount"
                    type="number"
                    min="0"
                    step="0.01"
                    defaultValue={modal.version?.maximum_amount}
                    placeholder="अमर्याद असल्यास रिकामे ठेवा"
                  />
                </label>
              </div>
              <input
                name="eligibility_config"
                type="hidden"
                value={JSON.stringify(modal.version?.eligibility_config || {})}
              />
              <input name="penalty_config" type="hidden" value="{}" />
              <input name="discount_config" type="hidden" value="{}" />
              <input
                name="exemption_config"
                type="hidden"
                value={JSON.stringify(modal.version?.exemption_config || {})}
              />
            </div>

            <footer>
              <button type="button" onClick={() => setModal(null)}>
                रद्द करा
              </button>
              <button className="atx-primary" disabled={busy}>
                {modal.version ? "आवृत्ती अद्ययावत करा" : "नवीन आवृत्ती मसुदा जतन करा"}
              </button>
            </footer>
          </form>
        </Modal>
      )}
      {modal?.type === "successor-approval" && (
        <Modal
          title={`${modal.rule.code} आवृत्ती ${modal.version.version_number} पुढील आवृत्ती म्हणून मंजूर करा`}
          onClose={() => setModal(null)}
          error={error}
        >
          <form className="atx-form" onSubmit={approveSuccessor}>
            <p>
              या मंजुरीमुळे आवृत्ती {modal.predecessor.version_number} ही{" "}
              <strong>{shortDate(modal.version.effective_from)}</strong> च्या
              आदल्या दिवशी बंद होईल आणि आवृत्ती {modal.version.version_number}{" "}
              मंजूर होईल. दोन्ही पडताळणी संकेत पुन्हा तपासले जातील.
            </p>
            <dl className="atx-review-pair">
              <div>
                <dt>मागील आवृत्ती</dt>
                <dd>
                  आवृत्ती {modal.predecessor.version_number} ·{" "}
                  {shortDate(modal.predecessor.effective_from)} – सध्या खुली
                </dd>
                <dd>
                  <code>{modal.predecessor.checksum}</code>
                </dd>
              </div>
              <div>
                <dt>पुढील आवृत्ती</dt>
                <dd>
                  आवृत्ती {modal.version.version_number} ·{" "}
                  {shortDate(modal.version.effective_from)} पासून
                </dd>
                <dd>
                  <code>{modal.version.checksum}</code>
                </dd>
              </div>
            </dl>
            <label>
              स्वतंत्र तपासणीचे कारण
              <textarea
                name="reason"
                required
                minLength="3"
                placeholder="कायदेशीर अधिकार, दिनांक, सूत्र आणि सातत्य पडताळले"
              />
            </label>
            <p className="atx-warning">
              हा मसुदा तयार करणारा अधिकारी तो मंजूर करू शकत नाही. पडताळणी संकेत
              बदलला असल्यास माहिती अद्ययावत करून पुन्हा तपासा.
            </p>
            <footer>
              <button type="button" onClick={() => setModal(null)}>
                रद्द करा
              </button>
              <button className="atx-primary" disabled={busy}>
                पुढील आवृत्ती मंजूर करा
              </button>
            </footer>
          </form>
        </Modal>
      )}
      {modal?.type === "demand-detail" && (
        <Modal
          title={`${demandTaxName(modal.demand)} · मागणी क्र. ${modal.demand.demand_number}`}
          onClose={() => setModal(null)}
          error={error}
        >
          <div className="atx-detail">
            <Status value={modal.demand.status} />
            <p>
              <strong>{demandSubjectName(modal.demand)}</strong>
              {demandSubjectReference(modal.demand) && (
                <> · {demandSubjectReference(modal.demand)}</>
              )}
            </p>
            <p>
              आर्थिक वर्ष {modal.demand.financial_year} · देय दिनांक{" "}
              {shortDate(modal.demand.due_date)}
            </p>
            <dl>
              <div>
                <dt>एकूण रक्कम</dt>
                <dd>{money(modal.demand.total_amount)}</dd>
              </div>
              <div>
                <dt>भरलेली रक्कम</dt>
                <dd>{money(modal.demand.paid_amount)}</dd>
              </div>
              <div>
                <dt>बाकी रक्कम</dt>
                <dd>{money(modal.demand.balance_amount)}</dd>
              </div>
            </dl>
            {modal.demand.payments?.length > 0 && <><h3>भरणा इतिहास</h3><DemandPaymentHistory demand={modal.demand} /></>}
            <h3>कर आकारणीचा तपशील</h3>
            {(modal.demand.display?.charge_lines?.length
              ? modal.demand.display.charge_lines
              : modal.demand.lines
            )?.map((line, i) => (
              <div className="atx-line" key={`${line.code || line.type}-${i}`}>
                <span>{line.name || demandTaxName(modal.demand)}</span>
                <strong>{money(line.amount)}</strong>
              </div>
            ))}
            <h3>ॲडजस्टमेंट नोंदी</h3>
            {modal.demand.adjustments?.some(item => item.status === "pending") && (
              <div style={{ padding: "8px 12px", marginBottom: "12px", borderRadius: "6px", backgroundColor: "#fff3cd", color: "#856404", border: "1px solid #ffeeba", fontSize: "0.85rem" }}>
                ⚠️ या मागणीवर ॲडजस्टमेंट मंजुरीसाठी प्रलंबित आहे. खालील "मंजूर करा" बटण दाबून त्वरित मंजूर करू शकता.
              </div>
            )}
            {modal.demand.adjustments?.map((item) => (
              <div className="atx-line" key={item.adjustment_uid}>
                <span>
                  <Status value={item.status} />{" "}
                  {adjustmentTypeLabel(item.adjustment_type)} · {item.reason}
                </span>
                <strong>{money(item.amount)}</strong>
                {item.status === "pending" && permissions.approve && (
                  <button
                    type="button"
                    style={{ marginLeft: 8, padding: "3px 9px", fontSize: "0.8rem", color: "#166534", backgroundColor: "#dcfce7", border: "1px solid #86efac", borderRadius: "4px", cursor: "pointer", fontWeight: 700 }}
                    onClick={() =>
                      action(
                        () =>
                          taxEngineAPI.approveAdjustment(
                            tenantSlug,
                            item.adjustment_uid,
                            {
                              reason: "ॲडजस्टमेंटचा पुरावा आणि मागणीची बाकी पडताळली",
                            },
                          ),
                        "ॲडजस्टमेंट तात्काळ मंजूर झाले आणि मागणी पुन्हा मोजली गेली.",
                      )
                    }
                  >
                    मंजूर करा
                  </button>
                )}
              </div>
            ))}
            {!modal.demand.adjustments?.length && <p>कोणतेही ॲडजस्टमेंट नाही.</p>}
            <details>
              <summary>तांत्रिक गणना नोंद (फक्त तपासणीसाठी)</summary>
              <pre>
                {JSON.stringify(modal.demand.calculation_snapshot, null, 2)}
              </pre>
            </details>
          </div>
        </Modal>
      )}
      {modal?.type === "adjustment" && (
        <Modal
          title={`मागणी क्र. ${modal.demand.demand_number} ॲडजस्टमेंट`}
          onClose={() => setModal(null)}
          error={error}
        >
          <AdjustmentForm
            demand={modal.demand}
            busy={busy}
            onCancel={() => setModal(null)}
            onSubmit={createAdjustment}
          />
        </Modal>
      )}
      {modal?.type === "manual" && (
        <Modal
          title={`मागणी क्र. ${modal.demand.demand_number} भरणा`}
          onClose={() => setModal(null)}
          error={error}
        >
          <ManualCollectionForm
            demand={modal.demand}
            busy={busy}
            tenantSlug={tenantSlug}
            onClose={() => setModal(null)}
            onSubmit={manualCollection}
          />
        </Modal>
      )}
      {modal?.type === "exemption" && (
        <Modal
          title={`कर सवलत · ${modal.account.account_number}`}
          onClose={() => setModal(null)}
          error={error}
        >
          <form
            className="atx-form"
            onSubmit={(event) => {
              event.preventDefault();
              const f = new FormData(event.currentTarget);
              let values;
              try {
                values = exemptionValues(f);
              } catch (err) {
                setError(err.message);
                return;
              }
              action(
                () =>
                  taxEngineAPI.createExemption(
                    tenantSlug,
                    modal.account.account_uid,
                    {
                      exemption_type: f.get("exemption_type"),
                      ...values,
                      valid_from: f.get("valid_from"),
                      valid_to: f.get("valid_to") || null,
                      reason: f.get("reason"),
                      evidence: { note: f.get("note") || null },
                    },
                  ),
                "कर सवलत मंजुरीसाठी तयार झाली.",
              );
            }}
          >
            <label>
              सवलतीचा प्रकार
              <input
                name="exemption_type"
                defaultValue="GOVERNMENT_APPROVED"
                required
              />
            </label>
            <div className="atx-form-row">
              <label>
                टक्केवारी
                <input
                  name="percentage"
                  type="number"
                  min=".0001"
                  max="100"
                  step=".0001"
                />
              </label>
              <label>
                किंवा निश्चित रक्कम
                <input name="fixed_amount" type="number" min=".01" step=".01" />
              </label>
            </div>
            <div className="atx-form-row">
              <label>
                पासून लागू
                <input name="valid_from" type="date" required />
              </label>
              <label>
                पर्यंत लागू
                <input name="valid_to" type="date" />
              </label>
            </div>
            <label>
              कारण
              <textarea name="reason" minLength="5" required />
            </label>
            <label>
              पुराव्याची टीप
              <textarea name="note" />
            </label>
            <footer>
              <button type="button" onClick={() => setModal(null)}>
                रद्द करा
              </button>
              <button className="atx-primary">सवलत मंजुरीसाठी तयार करा</button>
            </footer>
          </form>
        </Modal>
      )}
      {modal?.type === "receipt-action" && (
        <Modal
          title={`${modal.receipt.provider === "razorpay" ? "परतावा" : "उलट नोंद"} ${modal.receipt.receipt_number}`}
          onClose={() => setModal(null)}
          error={error}
        >
          <form
            className="atx-form"
            onSubmit={(event) => {
              event.preventDefault();
              const f = new FormData(event.currentTarget);
              const type =
                modal.receipt.provider === "razorpay"
                  ? "refund"
                  : f.get("action_type");
              action(
                () =>
                  taxEngineAPI.createReceiptAction(
                    tenantSlug,
                    modal.receipt.receipt_uid,
                    {
                      action_type: type,
                      amount: modal.receipt.amount,
                      reason: f.get("reason"),
                      external_reference:
                        f.get("external_reference") || undefined,
                    },
                    makeIdempotencyKey("receipt-action"),
                  ),
                "पावतीवरील कृती पूर्ण झाली किंवा ताळमेळासाठी नोंदवली गेली.",
              );
            }}
          >
            <p>
              फक्त संपूर्ण पावती रकमेवर कृती करता येते:{" "}
              <strong>{money(modal.receipt.amount)}</strong>
            </p>
            {modal.receipt.provider !== "razorpay" && (
              <label>
                कृती
                <select name="action_type">
                  <option value="reversal">उलट नोंद</option>
                  <option value="refund">परतावा</option>
                </select>
              </label>
            )}
            <label>
              कारण
              <textarea name="reason" required minLength="5" />
            </label>
            <label>
              बाह्य संदर्भ
              <input name="external_reference" />
            </label>
            <p className="atx-warning">
              वसुली करणारा अधिकारी किंवा करदाता स्वतःचा परतावा/उलट नोंद मंजूर
              करू शकत नाही. अनिश्चित ऑनलाइन स्थिती ताळमेळासाठी पाठवली जाईल.
            </p>
            <footer>
              <button type="button" onClick={() => setModal(null)}>
                रद्द करा
              </button>
              <button className="atx-primary" disabled={busy}>
                कृती निश्चित करा
              </button>
            </footer>
          </form>
        </Modal>
      )}
      {modal?.type === "appeal-decision" && (
        <Modal
          title={`अपीलचा अंतिम निर्णय · मागणी क्र. ${modal.appeal.demand?.demand_number}`}
          onClose={() => setModal(null)}
          error={error}
        >
          <AppealDecisionForm
            appeal={modal.appeal}
            busy={busy}
            onCancel={() => setModal(null)}
            onSubmit={(payload) =>
              action(
                () =>
                  taxEngineAPI.decideAppeal(
                    tenantSlug,
                    modal.appeal.appeal_uid,
                    payload,
                  ),
                payload.status === "rejected"
                  ? "अपील कारणासह नामंजूर करून वसुलीवरील स्थगिती समाप्त केली."
                  : "अपील निर्णय नोंदवला; संबंधित ॲडजस्टमेंटसाठी दुसऱ्या अधिकृत अधिकाऱ्याची मंजुरी आवश्यक आहे.",
              )
            }
          />
        </Modal>
      )}
      {modal?.type === "appeal-progress" && (
        <Modal
          title={`अपील कार्यवाही · मागणी क्र. ${modal.appeal.demand?.demand_number}`}
          onClose={() => setModal(null)}
          error={error}
        >
          <AppealProgressForm
            appeal={modal.appeal}
            initialStatus={modal.targetStatus}
            busy={busy}
            onCancel={() => setModal(null)}
            onSubmit={(payload) => appealProgress(modal.appeal, payload)}
          />
        </Modal>
      )}
      {modal?.type === "manual-clearance" && (
        <Modal
          title={`${paymentModeLabel(modal.attempt.payment_mode)} पडताळणी · ${money(modal.attempt.amount)}`}
          onClose={() => setModal(null)}
          error={error}
        >
          <form
            className="atx-form"
            onSubmit={(event) => {
              event.preventDefault();
              const f = new FormData(event.currentTarget),
                decision = f.get("action");
              action(
                () =>
                  taxEngineAPI.resolveManualCollection(
                    tenantSlug,
                    modal.attempt.attempt_uid,
                    {
                      action: decision,
                      version: modal.attempt.version,
                      reason: f.get("reason"),
                      cleared_on:
                        decision === "clear" ? f.get("cleared_on") : undefined,
                      clearance_reference:
                        decision === "clear"
                          ? f.get("clearance_reference")
                          : undefined,
                    },
                    makeIdempotencyKey("manual-clearance"),
                  ),
                decision === "clear"
                  ? "रक्कम जमा झाल्याची पडताळणी झाली; पावती, मागणी वाटप आणि आर्थिक नोंद एकत्र तयार झाली."
                  : "दस्तऐवज नामंजूर/न वटलेला म्हणून नोंदवला; कोणतीही कर किंवा आर्थिक नोंद तयार झाली नाही.",
              );
            }}
          >
            <div className="atx-evidence-card">
              <strong>{modal.attempt.payer?.legal_name}</strong>
              <small>{modal.attempt.payer?.party_uid}</small>
              <dl>
                <div>
                  <dt>भरणा पद्धत</dt>
                  <dd>{paymentModeLabel(modal.attempt.payment_mode)}</dd>
                </div>
                <div>
                  <dt>संदर्भ</dt>
                  <dd>{modal.attempt.external_reference}</dd>
                </div>
                <div>
                  <dt>बँक</dt>
                  <dd>{modal.attempt.instrument?.bank_name || "—"}</dd>
                </div>
                <div>
                  <dt>दस्तऐवज / हस्तांतरण दिनांक</dt>
                  <dd>
                    {modal.attempt.instrument?.instrument_date ||
                      modal.attempt.instrument?.payment_date ||
                      "—"}
                  </dd>
                </div>
              </dl>
            </div>
            <label>
              निर्णय
              <select name="action">
                <option value="clear">रक्कम जमा झाल्याची पुष्टी</option>
                <option value="reject">पुरावा / हस्तांतरण नामंजूर</option>
                {["cheque", "dd"].includes(modal.attempt.payment_mode) && (
                  <option value="bounce">न वटलेला म्हणून नोंदवा</option>
                )}
              </select>
            </label>
            <label>
              बँक विवरणपत्रातील जमा दिनांक
              <input
                name="cleared_on"
                type="date"
                defaultValue={new Date().toISOString().slice(0, 10)}
              />
            </label>
            <label>
              स्वतंत्र जमा पडताळणी संदर्भ
              <input
                name="clearance_reference"
                placeholder="बँक विवरणपत्र ओळ किंवा जमा क्रमांक"
              />
            </label>
            <label>
              तपासणीचे कारण
              <textarea name="reason" required minLength="5" />
            </label>
            <p className="atx-warning">
              जमा पडताळणी अंतिम आहे. आपण सादर केलेला दस्तऐवज किंवा स्वतःच्या
              करदाता सदस्यत्वावरील भरणा आपण तपासू शकत नाही.
            </p>
            <footer>
              <button type="button" onClick={() => setModal(null)}>
                रद्द करा
              </button>
              <button className="atx-primary" disabled={busy}>
                स्वतंत्र निर्णय नोंदवा
              </button>
            </footer>
          </form>
        </Modal>
      )}
      {modal?.type === "reconcile-intent" && (
        <Modal title="भरणा विनंतीचा ताळमेळ" onClose={() => setModal(null)} error={error}>
          <form
            className="atx-form"
            onSubmit={(event) => {
              event.preventDefault();
              const f = new FormData(event.currentTarget),
                act = f.get("action");
              action(
                () =>
                  taxEngineAPI.resolvePaymentIntent(
                    tenantSlug,
                    modal.intent.intent_uid,
                    {
                      action: act,
                      reason: f.get("reason"),
                      provider_payment_id:
                        f.get("provider_payment_id") || undefined,
                      provider_order_id:
                        f.get("provider_order_id") || undefined,
                      provider_confirmed_unfunded:
                        act === "cancel" && f.get("unfunded") === "on",
                    },
                  ),
                "भरणा विनंतीचा ताळमेळ अद्ययावत झाला.",
              );
            }}
          >
            <label>
              कृती
              <select name="action">
                <option value="retry_settlement">
                  पडताळलेला भरणा पुन्हा जमा करा
                </option>
                <option value="cancel">रक्कम न भरलेली विनंती रद्द करा</option>
              </select>
            </label>
            <label>
              प्रदात्याचा भरणा क्रमांक
              <input name="provider_payment_id" />
            </label>
            <label>
              प्रदात्याचा आदेश क्रमांक
              <input
                name="provider_order_id"
                defaultValue={modal.intent.provider_order_id}
              />
            </label>
            <label className="atx-check">
              <input name="unfunded" type="checkbox" /> प्रदात्याच्या प्रणालीत
              रक्कम अधिकृत किंवा जमा झालेली नसल्याची पुष्टी आहे
            </label>
            <label>
              कारण
              <textarea name="reason" required minLength="5" />
            </label>
            <footer>
              <button type="button" onClick={() => setModal(null)}>
                रद्द करा
              </button>
              <button className="atx-primary">मूळ विनंतीचा निकाल नोंदवा</button>
            </footer>
          </form>
        </Modal>
      )}
      {modal?.type === "reconcile-action" && (
        <Modal title="पावती कृतीचा ताळमेळ" onClose={() => setModal(null)} error={error}>
          <form
            className="atx-form"
            onSubmit={(event) => {
              event.preventDefault();
              const f = new FormData(event.currentTarget),
                resolution = f.get("resolution");
              action(
                () =>
                  taxEngineAPI.resolveReceiptAction(
                    tenantSlug,
                    modal.action.action_uid,
                    {
                      resolution,
                      reason: f.get("reason"),
                      provider_refund_id:
                        f.get("provider_refund_id") || undefined,
                      provider_confirmed_not_created:
                        resolution === "cancel_unissued" &&
                        f.get("not_created") === "on",
                    },
                  ),
                "पावती कृतीचा ताळमेळ अद्ययावत झाला.",
              );
            }}
          >
            <label>
              निकाल
              <select name="resolution">
                <option value="verify_provider">
                  प्रदात्याकडे पडताळून पूर्ण करा
                </option>
                <option value="cancel_unissued">
                  कधीही जारी न झालेली कृती रद्द करा
                </option>
              </select>
            </label>
            <label>
              प्रदात्याचा परतावा क्रमांक
              <input name="provider_refund_id" placeholder="rfnd_…" />
            </label>
            <label className="atx-check">
              <input name="not_created" type="checkbox" /> प्रदात्याच्या
              प्रणालीत परतावा तयार झाला नसल्याची पुष्टी आहे
            </label>
            <label>
              कारण
              <textarea name="reason" required minLength="5" />
            </label>
            <footer>
              <button type="button" onClick={() => setModal(null)}>
                रद्द करा
              </button>
              <button className="atx-primary">निकाल नोंदवा</button>
            </footer>
          </form>
        </Modal>
      )}
      {modal?.type === "issue-decision" && (
        <Modal
          title="खाते जोडणी समस्येचा निर्णय"
          onClose={() => setModal(null)}
          error={error}
        >
          <form
            className="atx-form"
            onSubmit={(event) => {
              event.preventDefault();
              const f = new FormData(event.currentTarget);
              action(
                () =>
                  identityAPI.decideIssue(tenantSlug, modal.issue.issue_uid, {
                    version: modal.issue.version,
                    status: f.get("status"),
                    resolution: f.get("resolution"),
                    revoke_access: f.get("revoke_access") === "on",
                  }),
                "खाते जोडणी समस्येचा निर्णय नोंदवला.",
              );
            }}
          >
            <p>
              <strong>{subjectTypeLabel(modal.issue.target_type)}</strong> ·{" "}
              {modal.issue.target_uid}
            </p>
            <p>{modal.issue.statement}</p>
            <label>
              निर्णय
              <select name="status">
                <option value="resolved">समस्या सोडवली</option>
                <option value="rejected">अहवाल नामंजूर</option>
              </select>
            </label>
            <label>
              निकाल
              <textarea name="resolution" required minLength="5" />
            </label>
            {[
              "incorrect_link",
              "incorrect_relationship",
              "duplicate_record",
            ].includes(modal.issue.issue_type) && (
              <label className="atx-check">
                <input name="revoke_access" type="checkbox" /> पडताळणीनंतर या
                वापरकर्त्याचे संबंधित व्यवसाय/कर प्रवेश अधिकार रद्द करा
              </label>
            )}
            <p className="atx-warning">
              प्रवेश रद्द करणे तत्काळ लागू होईल आणि त्याची नोंद जतन होईल. फक्त
              नोंदवही दुरुस्ती आवश्यक असल्यास हा पर्याय निवडू नका.
            </p>
            <footer>
              <button type="button" onClick={() => setModal(null)}>
                रद्द करा
              </button>
              <button className="atx-primary">निर्णय नोंदवा</button>
            </footer>
          </form>
        </Modal>
      )}
      <ExcelHouseholdTaxImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        tenantSlug={tenantSlug}
        onImportSuccess={() => {
          refresh();
          setMessage("एक्सेल आयात यशस्वीरित्या पूर्ण झाली! कर खाती व मागण्या अद्ययावत झाल्या आहेत.");
        }}
      />
    </section>
  );
}
